import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Button,
  Dropdown,
  Form,
  Row,
  Col,
  Alert,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Breadcrumb } from "../Breadcrumb/Breadcrumb";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import {
  getAccountPatients,
  getServicesByPracticeNumbers,
  getAppointmentsByPatientId,
  fetchallUsersByPractices,
  getPatientInvoiceICDs,
  getAppointmentById,
  updateAppointmentStatusByPatientId,
} from "../../api";
import {
  createInvoice,
  getInvoiceById,
  updateInvoice,
  deleteInvoice,
  sendInvoiceByEmail,
  updateClaim,
  getClaimByInvoiceId,
} from "../../afinanceapi";
import ICD10PickerModal from "../SearchICD10/SearchICD10";
import TariffSearch from "../Services/searchTariffs";
import NappiSearch from "../Services/searchNappi";
import { CompactMultiSelect } from "../MultiSelect/MultiSelect";
import { fetchAllmsrRates } from "../../medPracapi";
import "./CreateInvoice.css";
import "./acc-list.css";
import EmailInvoiceModal from "./EmailInvoice";
import { useNavigate, useLocation } from "react-router-dom";
import { getBookingsReturnPath } from "../../utils/bookingsReturn";
import AddModifierModal from "./Modifier";
import ServiceProviderModal from "./ServiceProviderModal";
import InvoiceAppointmentModal from "../FullCalendar/AppointmentPicker";
import AppointmentDateInput from "../FullCalendar/AppointmentDateInput";
import { isLineDateOnly } from "../../utils/appointmentTimes";
import CasePickerPanel, { normalizeToMMDDYYYY } from "../Cases/CasePickerPanel";
import SoonExpiringCasePrompt from "../Cases/SoonExpiringCasePrompt";
import { getCasesByPatientId } from "../../acases";
import { getModifierByDisciplineCode } from "../../afinanceapi";
import ServiceLinePicker from "../Services/ServiceLinePicker";
import { getUserPreferences } from "../BootNav/userPreferences";

// ServiceLinePicker lives in ../Services/ServiceLinePicker.jsx — it was
// extracted from this file so the appointment modals (AddAppointment /
// EditAppointments) can reuse the search experience without the custom-code
// toggle, via the hideCustomCodes prop.

function CreateInvoiceModal({
  account,
  practice,
  onInvoiceUpdated,
  initialInvoice,
  claim,
  placesOfService,
  selPatientId,
}) {
  // Core state

  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [customCodes, setCustomCodes] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(selPatientId);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const [selectedICD10s, setSelectedICD10s] = useState([]);
  const [lines, setLines] = useState([]);
  const [lineAppointments, setLineAppointments] = useState({});
  const [invoiceStatus, setInvoiceStatus] = useState(
    initialInvoice?.status || "Draft",
  );
  const [invoiceNumber, setInvoiceNumber] = useState(
    initialInvoice?.invoiceNumber || "",
  );
  const [invoiceDate, setInvoiceDate] = useState(
    initialInvoice?.invoiceDate || new Date().toISOString().split("T")[0],
  );
  const [practitioners, setPractioners] = useState([]);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState(
    initialInvoice?.practitioner?.id || "",
  );
  const [invoice, setInvoice] = useState(initialInvoice || null);
  const [invoiceErrors, setInvoiceErrors] = useState({});
  const [lineErrors, setLineErrors] = useState({});
  const [selectedPlaceOfService, setSelectedPlaceOfService] = useState("");
  const [showEmailInvoiceModal, setShowEmailInvoiceModal] = useState(false);
  const [invoiceBlob, setInvoiceBlob] = useState(null);
  const [feedback, setFeedback] = useState(claim);
  const { toast, showToast, hideToast } = useToast();
  const [outpatient, setOutpatient] = useState(true);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [attachedCase, setAttachedCase] = useState(null);
  const [showAddCase, setShowAddCase] = useState(false);
  const [showSoonExpiringPrompt, setShowSoonExpiringPrompt] = useState(false);
  const [soonExpiringCases, setSoonExpiringCases] = useState([]);
  const [inPatientCase, setInPatientCase] = useState(false);
  const [showModifier, setShowModifier] = useState(false);
  const [showServiceProvider, setShowServiceProvider] = useState(false);
  const [icdSelections, setIcdSelections] = useState([]);
  const [showICDModal, setShowICDModal] = useState(false);
  const [showLineApptModal, setShowLineApptModal] = useState(false);
  const [editingLineIndex, setEditingLineIndex] = useState(null);
  const [lineApptInitial, setLineApptInitial] = useState(null);
  const [cases, setCases] = React.useState([]);
  const { id, accountId } = useParams();
  const isNew = id === "1";
  const [busy, setBusy] = useState(null);
  const [modifierCatalog, setModifierCatalog] = useState(new Map());
  const [initialDisciplineCode, setInitialDisciplineCode] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Case sidebar collapse state. Default collapsed on mobile so the case
  // doesn't push the lines section below the fold; on desktop the CSS rule
  // `@media (min-width: 992px)` forces the body to stay open regardless.
  const [caseCollapsed, setCaseCollapsed] = useState(true);

  // New: default appointment template for subsequent lines
  const [defaultAppointmentTemplate, setDefaultAppointmentTemplate] =
    useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const initialInvoiceApp = location.state?.initialInvoiceApp;
  const incomingServiceId = location.state?.servicesId;
  console.log("State on load:", location.state);

  const canAccessAccounts = useMemo(() => {
    try {
      const token = sessionStorage.getItem("user");
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role)
          ? user.role
          : user?.role
            ? [user.role]
            : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some((r) => ["SuperAdmin", "Finance"].includes(r));
    } catch {
      return false;
    }
  }, []);

  const breadcrumbItems = useMemo(() => {
    if (!initialInvoice || isNew) return null;
    const invoiceLabel = `Invoice ${initialInvoice.invoiceNumber}`;

    if (location.state?.fromStatement) {
      const fi = (account?.accountFullNames || "").charAt(0);
      const accountName =
        location.state.accountName ??
        (`Account - ${fi ? fi + " " : ""}${account?.accountLastName || ""}`.trim() ||
          "Account");
      if (!accountName) return null;
      const statementCrumbs = [
        {
          label: accountName,
          path: `/accounts/${accountId}`,
          canAccess: canAccessAccounts,
        },
        {
          label: "Statement",
          path: `/accounts/${accountId}`,
          canAccess: canAccessAccounts,
        },
        { label: invoiceLabel },
      ];
      if (location.state?.fromAppointment) {
        return [
          { label: "Appointments", path: getBookingsReturnPath() },
          {
            label: location.state.appointmentLabel,
            path: location.state.appointmentPath,
          },
          ...statementCrumbs,
        ];
      }
      if (location.state?.fromDashboard) {
        return [{ label: "Dashboard", path: "/home" }, ...statementCrumbs];
      }
      return [
        { label: "Accounts", path: "/billing", canAccess: canAccessAccounts },
        ...statementCrumbs,
      ];
    }

    return [
      { label: "Invoices", path: "/billing", canAccess: canAccessAccounts },
      { label: invoiceLabel },
    ];
  }, [initialInvoice, account, accountId, isNew, location.state, canAccessAccounts]);

  // Appointment → FollowUpSession → Invoice flow: when we arrive with an
  // appointmentId in nav state, offer an opt-in "Complete appointment"
  // checkbox next to the save buttons. flowAppointment holds the fetched
  // appointment so we can hide the checkbox once it's already Completed.
  const flowAppointmentId = location.state?.appointmentId || null;
  const [flowAppointment, setFlowAppointment] = useState(null);
  // Opt-in: mark the linked appointment Completed when the invoice is saved.
  // Default driven by the user's `preferences.autoCompleteAppointments` flag
  // (off unless explicitly enabled), matching EditSession.jsx /
  // FollowUpSession.jsx so the two flows behave consistently.
  const [completeAppointmentChecked, setCompleteAppointmentChecked] =
    useState(!!getUserPreferences().autoCompleteAppointments);
  const showCompleteApptCheckbox =
    !!flowAppointmentId &&
    !!flowAppointment &&
    flowAppointment.status !== "Completed" &&
    flowAppointment.status !== "New Patient";

  // ======= Memoized derived values =======
  const primaryIcds = useMemo(
    () => icdSelections.filter((s) => s.isPrimary).map((s) => s.item),
    [icdSelections],
  );

  const secondaryIcds = useMemo(
    () => icdSelections.filter((s) => !s.isPrimary).map((s) => s.item),
    [icdSelections],
  );

  // Always parse using "." as decimal separator
  const normalizeToNumber = (val) => {
    if (val == null) return 0;

    const normalized = String(val)
      .replace(",", ".") // if user typed a comma, treat as "."
      .replace(/[^0-9.]/g, ""); // keep only digits and dot

    const num = parseFloat(normalized);
    return Number.isFinite(num) ? num : 0;
  };

  // Always format with "." and 2 decimals
  const formatAmount = (val) => {
    const num = normalizeToNumber(val);
    return num.toFixed(2); // JS spec: always uses "." as decimal
  };

  const totalAmount = useMemo(
    () =>
      Math.round(
        lines.reduce((total, line) => {
          const rate = normalizeToNumber(line.rate);
          return total + rate;
        }, 0) * 100,
      ) / 100,
    [lines],
  );

  const totalMedicalAidAmount = useMemo(
    () =>
      Math.round(
        lines.reduce((total, line) => {
          if (
            (account.accountMedicalAid || account.accountIODFund) &&
            !line.privatePayment &&
            (line.tariffCode || line.nappiCode)
          ) {
            const rate = normalizeToNumber(line.rate);
            return total + rate;
          }
          return total;
        }, 0) * 100,
      ) / 100,
    [lines, account.accountMedicalAid, account.accountIODFund],
  );

  const totalPatientAmount = useMemo(
    () =>
      Math.round(
        lines.reduce((total, line) => {
          if (
            (!account.accountMedicalAid && !account.accountIODFund) ||
            line.privatePayment ||
            (!line.tariffCode && !line.nappiCode)
          ) {
            const rate = normalizeToNumber(line.rate);
            return total + rate;
          }
          return total;
        }, 0) * 100,
      ) / 100,
    [lines, account.accountMedicalAid, account.accountIODFund],
  );

  const isPosted = invoiceStatus === "Paid" || invoiceStatus === "Posted";

  // ======= Utils (memoized) =======
  const getIcdCode = useCallback(
    (icd) => icd?.ICD10_Code || icd?.ICD10_3_Code || "",
    [],
  );

  const getIcdOneLine = useCallback((icd) => {
    const groupCode = icd.Group_Code?.split(" ")[0] ?? "";
    const groupDesc = icd.Group_Desc ?? "";
    const icd3Code = icd.ICD10_3_Code ?? "";
    const icd3Desc = icd.ICD10_3_Description ?? "";
    const code = icd.ICD10_Code ?? "";
    const desc = icd.ICD10_Description ?? "";
    return `${icd3Code} - ${icd3Desc} | ${
      code || icd3Code
    } - ${desc} | ${groupCode} - ${groupDesc}`;
  }, []);

  const splitDT = useCallback((iso) => {
    if (!iso) return { date: "", time: "" };
    const [date, timeFull = ""] = String(iso).split("T");
    return { date, time: timeFull.slice(0, 5) };
  }, []);

  // ======= Appointment helpers =======
  const apptErrorFor = (idx) =>
    idx == null
      ? ""
      : lineErrors?.[idx]?.appointmentDate ||
        lineErrors?.[idx]?.appointmentId ||
        "";

  const ensureAppointmentsForPatient = useCallback(
    async (patientId) => {
      if (!patientId) return [];
      if (lineAppointments[patientId]) {
        return lineAppointments[patientId];
      }
      const appointments = await getAppointmentsByPatientId(patientId);
      setLineAppointments((prev) => ({
        ...prev,
        [patientId]: appointments,
      }));
      return appointments;
    },
    [lineAppointments],
  );

  const createDefaultTemplate = useCallback((line) => {
    if (!line) return null;
    return {
      useCustomDate: line.useCustomDate,
      appointmentId: line.appointmentId || "",
      appointmentStartSet: line.appointmentStartSet || null,
      appointmentEndSet: line.appointmentEndSet || null,
      appointmentDateSet: line.appointmentDateSet || null,
      appointmentCustomStart: line.appointmentCustomStart || null,
      appointmentCustomEnd: line.appointmentCustomEnd || null,
      appointmentDateCustom: line.appointmentDateCustom || null,
      appointmentDate: line.appointmentDate || null,
    };
  }, []);

  const buildLineApptPatch = useCallback((sel) => {
    if (!sel) return {};
    if (!sel.useCustomDate) {
      const d = sel.appointmentStartSet
        ? sel.appointmentStartSet.slice(0, 10)
        : null;
      return {
        useCustomDate: false,
        appointmentId: sel.appointmentId || "",
        appointmentStartSet: sel.appointmentStartSet || null,
        appointmentEndSet: sel.appointmentEndSet || null,
        appointmentDateSet: d,
        appointmentCustomStart: null,
        appointmentCustomEnd: null,
        appointmentDateCustom: null,
        appointmentDate: d, // legacy
      };
    }
    const d = sel.appointmentCustomStart
      ? sel.appointmentCustomStart.slice(0, 10)
      : null;
    return {
      useCustomDate: true,
      appointmentId: null,
      appointmentStartSet: null,
      appointmentEndSet: null,
      appointmentDateSet: null,
      appointmentCustomStart: sel.appointmentCustomStart || null,
      appointmentCustomEnd: sel.appointmentCustomEnd || null,
      appointmentDateCustom: d,
      appointmentDate: d, // legacy
    };
  }, []);

  const setLineFields = useCallback((idx, patch) => {
    setLines((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    );
  }, []);

  // Shape for AppointmentDateInput value
  const lineApptValue = useCallback(
    (line) => {
      if (!line) return null;
      if (line.useCustomDate) {
        const s = splitDT(line.appointmentCustomStart);
        const e = splitDT(line.appointmentCustomEnd);
        return {
          useCustomDate: true,
          startDate: s.date,
          startTime: s.time,
          endDate: e.date,
          endTime: e.time,
        };
      }
      const s = splitDT(
        line.appointmentStartSet ||
          (line.appointmentDateSet ? `${line.appointmentDateSet}T00:00` : ""),
      );
      const e = splitDT(line.appointmentEndSet);
      return {
        useCustomDate: false,
        startDate: s.date,
        startTime: s.time,
        endDate: e.date,
        endTime: e.time,
      };
    },
    [splitDT],
  );
  // ======= Validation =======

  const runGuarded = async (key, fn) => {
    if (busy) return; // ignore double clicks
    // Only set busy **after** validations pass inside fn (see below)
    await fn(
      async () => setBusy(key),
      () => setBusy(null),
    );
  };

  // In validation, check the ref instead
  const validateInvoiceFields = useCallback(
    (currentValues = {}) => {
      const {
        invoiceDate: checkInvoiceDate = invoiceDate,
        selectedPractice: checkPractice = selectedPracticeId,
        selectedPractitionerId: checkPractitionerId = selectedPractitionerId,
        selectedPatientId: checkPatientId = selectedPatientId,
        selectedPlaceOfService: checkPlaceOfService = selectedPlaceOfService,
        icdSelections: checkIcdSelections = icdSelections,
      } = currentValues;

      const newErrors = {};

      if (!checkInvoiceDate)
        newErrors.invoiceDate = "Invoice date is required.";
      if (!checkPractice) newErrors.practice = "Practice is required.";
      if (!checkPractitionerId)
        newErrors.practitioner = "Practitioner is required.";
      if (!checkPatientId) newErrors.patient = "Patient is required.";
      if (!checkPlaceOfService)
        newErrors.placeOfService = "Place of service is required.";

      if (account.accountMedicalAid && checkIcdSelections?.length === 0) {
        newErrors.icd10 =
          "At least one ICD10 code is required for medical aid patients.";
      }

      // Check if primary ICD10 starts with S or T - requires external cause code (V, W, X, Y)
      if (account.accountMedicalAid && checkIcdSelections?.length > 0) {
        const primaryCodes = checkIcdSelections
          .filter((s) => s.isPrimary)
          .map((s) => s.item?.ICD10_Code || s.item?.ICD10_3_Code || "");
        const secondaryCodes = checkIcdSelections
          .filter((s) => !s.isPrimary)
          .map((s) => s.item?.ICD10_Code || s.item?.ICD10_3_Code || "");

        const hasSorTPrimary = primaryCodes.some(
          (code) => code.startsWith("S") || code.startsWith("T"),
        );
        const hasExternalCauseSecondary = secondaryCodes.some((code) =>
          ["V", "W", "X", "Y"].some((letter) => code.startsWith(letter)),
        );

        if (hasSorTPrimary && !hasExternalCauseSecondary) {
          newErrors.icd10 =
            "When primary ICD10 starts with S or T (injury), a secondary ICD10 starting with V, W, X, or Y (external cause) is required.";
        }
      }

      setInvoiceErrors(newErrors);
      return Object.keys(newErrors)?.length === 0;
    },
    [
      invoiceDate,
      selectedPractice,
      selectedPractitionerId,
      selectedPatientId,
      selectedPlaceOfService,
      account.accountMedicalAid,
      icdSelections,
    ],
  );

  const validateInvoiceLines = useCallback(() => {
    const errors = {};
    let hasError = false;

    if (!Array.isArray(lines) || lines?.length === 0) {
      alert("At least one invoice line is required.");
      return false;
    }

    const hasVal = (v) =>
      v != null && (typeof v !== "string" || v.trim() !== "");

    lines.forEach((line, idx) => {
      const lineError = {};

      // ✅ If NOTHING has been chosen/entered yet, block immediately
      const missingAllApptFields =
        !hasVal(line.appointmentId) &&
        !hasVal(line.appointmentStartSet) &&
        !hasVal(line.appointmentDateSet) &&
        !hasVal(line.appointmentCustomStart) &&
        !hasVal(line.appointmentCustomEnd);

      if (missingAllApptFields) {
        lineError.appointmentDate =
          "Select an appointment or enter a custom start & end time.";
      } else if (line.useCustomDate) {
        // === Custom date-time path ===
        const s = line.appointmentCustomStart;
        const e = line.appointmentCustomEnd;

        if (!hasVal(s) || !hasVal(e)) {
          lineError.appointmentDate = "Start and end time are required.";
        } else {
          const ds = new Date(s);
          const de = new Date(e);
          // Date-only lines (No Times mode) store both ends at midnight, so
          // same-day equality is valid; only strict regression is an error.
          const dateOnly = isLineDateOnly(line);
          if (Number.isNaN(ds.getTime()) || Number.isNaN(de.getTime())) {
            lineError.appointmentDate = "Invalid date/time.";
          } else if (dateOnly ? de < ds : de <= ds) {
            lineError.appointmentDate = dateOnly
              ? "End date must be on or after start date."
              : "End time must be after start time.";
          }
        }
      } else {
        // === Linked appointment path ===
        const hasAppt =
          hasVal(line.appointmentId) ||
          hasVal(line.appointmentStartSet) ||
          hasVal(line.appointmentDateSet);

        if (!hasAppt) {
          lineError.appointmentId = "Appointment is required.";
        }
      }
      // In your validation function, add this logging inside the forEach:
      console.log(`Line ${idx}:`, {
        appointmentId: line.appointmentId,
        appointmentStartSet: line.appointmentStartSet,
        appointmentDateSet: line.appointmentDateSet,
        appointmentCustomStart: line.appointmentCustomStart,
        appointmentCustomEnd: line.appointmentCustomEnd,
        missingAllApptFields,
      });

      // === Existing checks ===
      if (
        account.accountMedicalAid &&
        !line.privatePayment &&
        !hasVal(line.icd101)
      ) {
        lineError.icd101 = "ICD10 code is required.";
      }

      // Check if line primary ICD10 starts with S or T - requires external cause code (V, W, X, Y)
      if (
        account.accountMedicalAid &&
        !line.privatePayment &&
        hasVal(line.icd101)
      ) {
        const primaryCode = String(line.icd101 || "").toUpperCase();
        if (primaryCode.startsWith("S") || primaryCode.startsWith("T")) {
          const secondaryCodes = Array.isArray(line.icd10Secondary)
            ? line.icd10Secondary
            : [];
          const hasExternalCause = secondaryCodes.some((code) =>
            ["V", "W", "X", "Y"].some((letter) =>
              String(code || "")
                .toUpperCase()
                .startsWith(letter),
            ),
          );
          if (!hasExternalCause) {
            lineError.icd10Secondary =
              "Primary ICD10 starting with S or T requires a secondary code starting with V, W, X, or Y.";
          }
        }
      }

      if (!hasVal(line.description)) {
        lineError.description = "Description is required.";
      }

      const qty = Number(line.quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        lineError.quantity = "Quantity must be greater than 0.";
      }

      const rateNum = parseFloat(String(line.rate ?? "").replace(/[, ]+/g, ""));
      if (!Number.isFinite(rateNum) || rateNum < 0) {
        lineError.rate = "Rate must be 0 or greater.";
      }

      if (!selectedPlaceOfService) {
        lineError.placeOfService = "Place of Service is required.";
      }

      if (Object.keys(lineError)?.length) {
        errors[idx] = lineError;
        hasError = true;
      }
    });

    setLineErrors(errors);
    return !hasError;
  }, [lines, account.accountMedicalAid, selectedPlaceOfService]);

  // EDI disabled → default new lines to private
  const isEdiDisabled = useMemo(() => {
    if (!account?.ediDisabled || !account?.ediEndDate) return false;
    return (
      new Date(account.ediEndDate) >=
      new Date(new Date().toISOString().split("T")[0])
    );
  }, [account?.ediDisabled, account?.ediEndDate]);

  // ======= Generic add line with defaults =======
  const addLineWithDefaults = useCallback(
    async (type, additionalFields = {}) => {
      if (!validateInvoiceFields()) return;

      const patientId = selectedPatientId;

      const nextLineNumber = (lines?.length || 0) + 1;

      const baseLine = {
        lineNumber: nextLineNumber,
        type,
        patientId,
        icd101: primaryIcds[0] ? getIcdCode(primaryIcds[0]) : "",
        icd10Secondary: secondaryIcds.map(getIcdCode),
        oti: "A",
        quantity: 1,
        rate: "0.00",
        rateManuallyEdited: false,
        privatePayment: isEdiDisabled,
        // Always start with empty appointment fields - user must select
        appointmentId: "",
        useCustomDate: false,
        appointmentStartSet: null,
        appointmentEndSet: null,
        appointmentDateSet: null,
        appointmentCustomStart: null,
        appointmentCustomEnd: null,
        appointmentDateCustom: null,
        appointmentDate: null,
        ...additionalFields,
      };

      setLines((prev) => [...prev, baseLine]);
    },
    [
      validateInvoiceFields,
      selectedPatientId,
      lines?.length,
      primaryIcds,
      secondaryIcds,
      getIcdCode,
      isEdiDisabled,
    ],
  );

  // ======= Simplified line add handlers =======
  const handleAddLine = useCallback(() => {
    addLineWithDefaults("custom", { tariffCode: "", description: "" });
  }, [addLineWithDefaults]);

  const handleAddServiceLine = useCallback(() => {
    addLineWithDefaults("service", {
      serviceId: "",
      selectedService: null,
      tariffCode: "",
      description: "",
    });
  }, [addLineWithDefaults]);

  const handleAddTariffLine = useCallback(() => {
    addLineWithDefaults("tariff", {
      selectedTariff: null,
      tariffCode: "",
      description: "",
      practiceNumber: selectedPractice?.practiceNumber || null,
    });
  }, [addLineWithDefaults, selectedPractice?.practiceNumber]);

  const handleAddNappiLine = useCallback(() => {
    addLineWithDefaults("nappi", {
      selectedNappi: null,
      nappiCode: "",
      description: "",
    });
  }, [addLineWithDefaults]);

  // If the first line appointment changes, update template
  const handleLineAppointmentChange = useCallback(
    (index, appointmentData) => {
      setLines((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;

        // Update the edited line
        updated[index] = { ...updated[index], ...appointmentData };

        const gid = updated[index].groupId;
        if (!gid) return updated; // not a grouped service; nothing to propagate

        // Find the group's lines and the first (smallest lineNumber)
        const groupIndices = updated
          .map((l, i) => ({ i, l }))
          .filter(({ l }) => l.groupId === gid);

        if (groupIndices?.length <= 1) return updated;

        const firstLineNumberInGroup = Math.min(
          ...groupIndices.map(({ l }) => l.lineNumber || 0),
        );
        const isFirstInGroup =
          (updated[index].lineNumber || 0) === firstLineNumberInGroup;

        // Only the first line auto-propagates
        if (!isFirstInGroup) return updated;

        // Build a normalized "template" from the edited line
        const patch = createDefaultTemplate(updated[index]);

        // Apply to the rest of the group (excluding the edited one)
        groupIndices.forEach(({ i: gi }) => {
          if (gi === index) return;
          updated[gi] = { ...updated[gi], ...patch };
        });

        return updated;
      });

      // Keep the global default in sync (handy for future new lines)
      setDefaultAppointmentTemplate((prevTpl) => {
        const nextTpl = {
          ...(prevTpl || {}),
          ...createDefaultTemplate({ ...lines[index], ...appointmentData }),
        };
        return nextTpl;
      });
    },
    [lines, createDefaultTemplate],
  );

  // ======= Appointment-driven Place of Service =======
  // Collect the unique non-empty placeOfService values from every line that
  // currently has an appointment selected. Two sources, in priority:
  //   1. line.placeOfService — snapshotted by AppointmentPicker.onConfirm
  //      at the moment the user picks an appointment. Always present for
  //      fresh selections, so this is the hot path.
  //   2. lineAppointments cache lookup — used for loaded existing invoices
  //      that pre-date the snapshot field, or any line where the snapshot
  //      didn't carry through. The cache may not be populated for the line's
  //      patient (preload only covers patients[0]), so this is best-effort.
  // Empty / missing values are ignored so we don't false-positive a conflict
  // on legacy appointments that don't have placeOfService at all.
  //
  // Appointment _id arrives in three shapes depending on how the doc was
  // serialised upstream: a plain hex string, an ObjectId-like object with
  // `_id.$oid`, or a sibling `id` field. Mirror the same normalisation
  // AppointmentPicker.normId uses so the find() actually matches.
  const normApptId = (a) =>
    String(a?._id?.$oid || a?._id || a?.id || "");

  const appointmentPlaceOfServices = useMemo(() => {
    const set = new Set();
    for (const line of lines) {
      if (!line?.appointmentId) continue;
      let pos = line.placeOfService
        ? String(line.placeOfService).trim()
        : "";
      if (!pos && line.patientId) {
        const arr = lineAppointments[line.patientId];
        if (Array.isArray(arr)) {
          const targetId = String(line.appointmentId);
          const appt = arr.find((a) => normApptId(a) === targetId);
          const fromAppt = appt?.placeOfService || appt?.location?.placeOfService;
          if (fromAppt) pos = String(fromAppt).trim();
        }
      }
      if (pos) set.add(pos);
    }
    return Array.from(set);
  }, [lines, lineAppointments]);

  const hasPlaceOfServiceConflict = appointmentPlaceOfServices.length > 1;

  // When exactly one unique placeOfService exists across all selected
  // appointments, mirror it onto the invoice. When two or more conflict,
  // leave the field alone — the warning below the dropdown tells the user
  // to pick which one applies. Outpatient flag is kept in sync the same
  // way it is when the user changes the dropdown manually.
  //
  // Why the ref + no `selectedPlaceOfService` dep: the user must be able to
  // override the appointment-derived POS via the dropdown. If we depended on
  // `selectedPlaceOfService`, every manual change would re-fire this effect,
  // see `onlyPos !== selectedPlaceOfService`, and snap the field back. The
  // ref tracks the last appointment-driven value we synced, so we only push a
  // new value when the *appointment*'s POS changes — not when the user edits
  // the dropdown.
  const lastSyncedApptPosRef = useRef(null);
  useEffect(() => {
    if (appointmentPlaceOfServices.length !== 1) {
      lastSyncedApptPosRef.current = null;
      return;
    }
    const onlyPos = appointmentPlaceOfServices[0];
    if (!onlyPos) return;
    if (lastSyncedApptPosRef.current === onlyPos) return;
    lastSyncedApptPosRef.current = onlyPos;
    setSelectedPlaceOfService(onlyPos);
    const matched = placesOfService.find((p) => p.HCFA_code === onlyPos);
    if (matched) setOutpatient(matched.out ? "O" : "I");
  }, [appointmentPlaceOfServices, placesOfService]);

  // ======= Service / tariff / nappi helpers =======
  const DEFAULT_MSR_YEAR = String(new Date().getFullYear()); // "2026" right now

  function getLineServiceDate(line, fallbackInvoiceDate) {
    // Prefer appointment-derived fields (custom > set), then legacy appointmentDate, then invoiceDate
    const d =
      line?.appointmentDateCustom ||
      line?.appointmentDateSet ||
      line?.appointmentDate ||
      fallbackInvoiceDate ||
      "";

    return String(d).slice(0, 10); // "YYYY-MM-DD"
  }

  function getYearFromYYYYMMDD(dateStr, fallbackYear = DEFAULT_MSR_YEAR) {
    const y = String(dateStr || "").slice(0, 4);
    return /^\d{4}$/.test(y) ? y : fallbackYear;
  }

  const enrichModifiers = (mods = [], catalog /* Map */) => {
    const arr = Array.isArray(mods) ? mods : [];
    return arr
      .map((m) => {
        if (!m) return null;
        const code = typeof m === "string" ? m : m.code;
        if (!code) return null;

        const meta = catalog?.get(String(code));
        return meta
          ? { code: String(code), ...meta } // ✅ now computable
          : { code: String(code) }; // fallback: code-only
      })
      .filter(Boolean);
  };

  const getMSRRate = useCallback(
    async (
      practiceNumber,
      tariffCode,
      planOptionCode,
      IODOptionCode,
      year,
      practiceContract,
    ) => {
      const rateData = await fetchAllmsrRates(
        practiceNumber,
        [tariffCode],
        planOptionCode || IODOptionCode,
        year,
        practiceContract,
      );
      // Normalize practiceContract to an array of lowercase strings (handles string or array)
      const contracts = Array.isArray(practiceContract)
        ? practiceContract.map((c) => String(c).toLowerCase().trim())
        : [
            String(practiceContract || "msr")
              .toLowerCase()
              .trim(),
          ];
      const hasNnw = contracts.includes("nnw");
      const hasJsp = contracts.includes("jsp");

      const pickEntry = (type) =>
        (rateData || []).find(
          (e) => (e.priceGroupType || "").toString().toUpperCase() === type,
        );

      const getRateAndQty = (entry) => {
        if (!entry) return { rate: 0, quantity: 1 };

        const quantity = Number(entry.numberOfUnits) || 1;

        // Your priority order (unchanged), but coerced to numbers safely
        const schemeFixed = Number(entry.tariffRandSchemeFixed);
        const rcfRate = Number(entry.tariffRandRcfSchemeRate);
        const calculated = Number(entry.tariffRandCalculated);

        let rate = 0;
        if (Number.isFinite(schemeFixed) && schemeFixed > 0) rate = schemeFixed;
        else if (Number.isFinite(rcfRate) && rcfRate > 0) rate = rcfRate;
        else if (Number.isFinite(calculated) && calculated > 0)
          rate = calculated;

        return { rate, quantity };
      };

      // Priority matrix (always falls through to MSR as final fallback):
      //   ["nnw","jsp"]        → JSP → NNW → MSR
      //   ["jsp"] / ["msr","jsp"] → JSP → MSR
      //   ["nnw"]              → NNW → MSR
      //   ["msr"] / null       → MSR
      let rate = 0,
        quantity = 1;
      if (hasJsp && hasNnw) {
        ({ rate, quantity } = getRateAndQty(pickEntry("JSP")));
        if (!rate || rate <= 0)
          ({ rate, quantity } = getRateAndQty(pickEntry("NNW")));
        if (!rate || rate <= 0)
          ({ rate, quantity } = getRateAndQty(pickEntry("MSR")));
      } else if (hasJsp) {
        ({ rate, quantity } = getRateAndQty(pickEntry("JSP")));
        if (!rate || rate <= 0)
          ({ rate, quantity } = getRateAndQty(pickEntry("MSR")));
      } else if (hasNnw) {
        ({ rate, quantity } = getRateAndQty(pickEntry("NNW")));
        if (!rate || rate <= 0)
          ({ rate, quantity } = getRateAndQty(pickEntry("MSR")));
      } else {
        ({ rate, quantity } = getRateAndQty(pickEntry("MSR")));
      }

      return {
        rate: Number.isFinite(rate) ? Number(rate.toFixed(2)) : 0,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      };
    },
    [],
  );

  const normalizePct = (v) => {
    if (v == null) return 0;
    if (typeof v === "string") return parseFloat(v.replace("%", "")) || 0;
    if (typeof v === "number") return v;
    return 0;
  };

  // Build a catalog: code -> { adjustment, adjustmentPercentage }
  const buildModifierCatalog = (mods = []) => {
    const map = new Map();
    (mods || []).forEach((m) => {
      if (!m?.code) return;
      map.set(String(m.code), {
        adjustment: m.adjustment,
        adjustmentPercentage: m.adjustmentPercentage,
      });
    });
    return map;
  };

  const repriceLineFromMSR = (line, msr, modifierCatalog /* Map */) => {
    // MSR base
    const base = Number(msr?.rate ?? 0);
    const baseRate = Number.isFinite(base) ? base : 0;

    const existing = Array.isArray(line.modifier) ? line.modifier : [];
    const rebuiltModifiers = existing
      .map((m) => (typeof m === "string" ? { code: m } : m))
      .filter((m) => m?.code)
      .map((m) => {
        const code = String(m.code);

        // Option A: meta already on the line
        const adj = m.adjustment ?? modifierCatalog?.get(code)?.adjustment;
        const pctVal =
          m.adjustmentPercentage ??
          modifierCatalog?.get(code)?.adjustmentPercentage;

        const pct = normalizePct(pctVal) / 100;

        // If we still don't know how to compute, keep code only (display-only)
        if (!adj || !pct) return { ...m, code, rateChange: 0 };

        const rateChange =
          adj === "up"
            ? +(baseRate * pct).toFixed(2)
            : adj === "down"
              ? +(-baseRate * pct).toFixed(2)
              : 0;

        return {
          ...m,
          code,
          adjustment: adj,
          adjustmentPercentage: pctVal,
          rateChange,
        };
      });

    const totalChange = rebuiltModifiers.reduce(
      (sum, m) => sum + (Number(m.rateChange) || 0),
      0,
    );

    const finalRate = +(baseRate + totalChange).toFixed(2);

    return {
      ...line,
      quantity: msr?.quantity ?? line.quantity ?? 1,
      originalRate: baseRate.toFixed(2), // MSR base
      rate: finalRate.toFixed(2), // base + modifiers
      modifier: rebuiltModifiers,
    };
  };

  const refreshMSRForLine = useCallback(
    async (index, appointmentPatch = null) => {
      if (!selectedPractice?.practiceNumber) return;
      if (!account?.accountMedicalAid && !account?.accountIODFund) return;

      const currentLine = lines[index];
      if (!currentLine) return;

      // skip private or nappi
      if (currentLine.privatePayment) return;
      if (currentLine.type === "nappi" || !!currentLine.nappiCode) return;

      // skip if service uses flat rate only (not medical aid rate)
      const paymentOptions =
        currentLine.selectedService?.pricing?.paymentOptions;
      const useFlatRate = paymentOptions?.useFlatRate;
      const useFlatAndMedicalAidRate = paymentOptions?.useFlatAndMedicalAidRate;
      if (useFlatRate && !useFlatAndMedicalAidRate) return;

      const tariffCode =
        currentLine.tariffCode || currentLine.selectedTariff?.code || "";
      if (!tariffCode) return;

      const merged = appointmentPatch
        ? { ...currentLine, ...appointmentPatch }
        : currentLine;

      if (merged.rateManuallyEdited) return;

      const fallbackInvoiceDate =
        invoiceDate || new Date().toISOString().slice(0, 10);

      const lineDate = getLineServiceDate(merged, fallbackInvoiceDate);
      const msrYear = getYearFromYYYYMMDD(lineDate);

      const msr = await getMSRRate(
        selectedPractice.practiceNumber,
        tariffCode,
        account.accountMedicalAidOptionCode,
        account.accountIODOptionCode,
        msrYear,
        selectedPractice.practiceContract,
      );

      // Build modifier catalog for this practice/discipline OR from selectedService
      const catalog = buildModifierCatalog(
        merged?.selectedService?.pricing?.selectedModifiers || [],
      );

      setLines((prev) => {
        const updated = [...prev];
        const lineNow = updated[index];
        if (!lineNow) return prev;

        // avoid overwriting if tariff changed while awaiting
        const nowTariff =
          lineNow.tariffCode || lineNow.selectedTariff?.code || "";
        if (String(nowTariff) !== String(tariffCode)) return prev;

        updated[index] = repriceLineFromMSR(lineNow, msr, catalog);
        return updated;
      });
    },
    [
      selectedPractice?.practiceNumber,
      account?.accountMedicalAid,
      account?.accountMedicalAidOptionCode,
      lines,
      invoiceDate,
      getMSRRate,
      getLineServiceDate,
      getYearFromYYYYMMDD,
    ],
  );

  const refreshMSRForGroup = useCallback(
    async (groupId, appointmentPatch) => {
      const groupIndexes = lines
        .map((l, i) => ({ l, i }))
        .filter(({ l }) => l.groupId === groupId)
        .map(({ i }) => i);

      // refresh each line (tariff lines will update; nappi/private will no-op)
      for (const i of groupIndexes) {
        await refreshMSRForLine(i, appointmentPatch);
      }
    },
    [lines, refreshMSRForLine],
  );

  const handleServiceSelect = useCallback(
    async (index, serviceId) => {
      const selectedService =
        services.find((s) => s._id === serviceId) ||
        customCodes.find((s) => s._id === serviceId);
      if (!selectedService) return;
      console.log("Selected service:", selectedService);

      // --- Service modifiers (tariff lines only) ---
      const serviceModifiers = selectedService.pricing?.selectedModifiers || [];

      const isNappiLine = (l) => l?.type === "nappi" || !!l?.nappiCode;
      const isTariffLine = (l) => !isNappiLine(l);

      // Applies modifiers to a line.
      // Supports two shapes of modifier inputs:
      //
      // 1) FULL pricing modifiers (can change rate):
      //    { code: "0003", adjustment: "up"|"down", adjustmentPercentage: 10 | "10%" }
      //
      // 2) CODE-ONLY modifiers (informational only, DOES NOT change rate):
      //    { code: "0008" }   or   "0008"
      //
      // If you pass only code-only modifiers (like the ones in your selectedTariffs[].modifiers),
      // the function will attach them to line.modifier but will NOT change rate.
      const applyModifiersToLine = (line, modifiersInput = []) => {
        const modsArr = Array.isArray(modifiersInput) ? modifiersInput : [];

        // Normalize to objects: { code, adjustment?, adjustmentPercentage? }
        const normalizedMods = modsArr
          .map((m) => {
            if (!m) return null;
            if (typeof m === "string") return { code: m };
            if (typeof m === "object") return { ...m };
            return null;
          })
          .filter((m) => m && m.code);

        // Nothing to do
        if (!normalizedMods?.length) return line;

        // Determine base rate (number)
        let baseRate = parseFloat(line.originalRate ?? line.rate ?? "0");
        if (Number.isNaN(baseRate)) baseRate = 0;

        // Start from existing modifiers if present
        const existingMods = Array.isArray(line.modifier)
          ? line.modifier
              .map((m) => (typeof m === "string" ? { code: m } : m))
              .filter((m) => m && m.code)
          : [];

        // We will replace/merge by code (avoid duplicates)
        const byCode = new Map();
        existingMods.forEach((m) => byCode.set(String(m.code), m));

        // Track whether we have enough info to compute rate changes
        let canComputeAny = false;
        let totalChange = 0;

        normalizedMods.forEach((mod) => {
          const code = String(mod.code);

          // Merge into map (latest wins)
          const merged = {
            ...(byCode.get(code) || {}),
            ...mod,
            code,
          };

          // See if this modifier is "computable"
          // Needs BOTH adjustment AND adjustmentPercentage
          const hasAdj =
            merged.adjustment === "up" || merged.adjustment === "down";
          let rawPct = merged.adjustmentPercentage;

          // Parse "10%" / "10" / 10
          let pctNum = 0;
          if (typeof rawPct === "string") {
            pctNum = parseFloat(rawPct.replace("%", ""));
          } else if (typeof rawPct === "number") {
            pctNum = rawPct;
          }

          const hasPct = Number.isFinite(pctNum) && pctNum !== 0;

          if (hasAdj && hasPct) {
            canComputeAny = true;

            const pct = pctNum / 100;
            let rateChange = 0;

            if (merged.adjustment === "up") rateChange = baseRate * pct;
            else if (merged.adjustment === "down") rateChange = -baseRate * pct;

            rateChange = Number(rateChange.toFixed(2));
            totalChange += rateChange;

            // Persist the computed change on the modifier object
            merged.rateChange = rateChange;
          } else {
            // Code-only or insufficient info → ensure no stale rateChange
            if ("rateChange" in merged) delete merged.rateChange;
          }

          byCode.set(code, merged);
        });

        const finalMods = Array.from(byCode.values());

        // If we can't compute any modifier impacts, don't touch pricing.
        if (!canComputeAny) {
          return {
            ...line,
            modifier: finalMods,
            // preserve originalRate/rate as-is
          };
        }

        const newRate = Number((baseRate + totalChange).toFixed(2));

        return {
          ...line,
          modifier: finalMods,
          originalRate: line.originalRate ?? baseRate.toFixed(2),
          rate: newRate.toFixed(2),
        };
      };

      // --- Group id for service-created rows ---
      const makeGroupId = () =>
        `svc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;

      const groupId = makeGroupId();

      const tariffs = selectedService.pricing?.selectedTariffs || [];
      const useFlatRate = selectedService.pricing?.paymentOptions?.useFlatRate;
      const useFlatAndMedicalAidRate =
        selectedService.pricing?.paymentOptions?.useFlatAndMedicalAidRate;

      const fallbackDescription =
        selectedService.pricing?.invoiceDescription || "";
      const fallbackFlatRateString = selectedService.pricing?.flatRate || "R 0";
      const fallbackFlatRate =
        parseFloat(fallbackFlatRateString.replace(/[^\d.]/g, "")) || 0;

      const isMedicalAid = !!account?.accountMedicalAid;
      const isIOD = !!account?.accountIODFund;
      // Insurance accounts (IOD-flagged but with category "INSURANCE") always
      // bill the service's flat-rate amount per line, regardless of tariffs or
      // MSR rates. We route them through the private/flat-rate cases (CASE 1/2).
      const isInsurance =
        isIOD &&
        (account?.accountIODFundCategory || "").toUpperCase() === "INSURANCE";

      // Custom codes carry a forced description of "<code> - <invoiceDescription>"
      // on every line. The pricing routing (CASE 1-4) honours the code's own
      // paymentOptions exactly like a regular service — "Flat Rate" stays
      // private-flow, "Flat + Medical Aid" pulls MSR rates per tariff.
      const isCustomCode = selectedService.serviceType === "custom_code";
      const customCodeDescription = isCustomCode
        ? [selectedService.servicesName || "", fallbackDescription]
            .filter(Boolean)
            .join(" - ")
        : null;
      const descriptionFor = (preferred) =>
        isCustomCode
          ? customCodeDescription
          : (preferred || fallbackDescription);
      // IOD accounts use the same per-tariff MSR-rate / MSR-quantity routing as
      // medical aid: getMSRRate already accepts accountIODOptionCode (see the
      // CASE 3/4 calls below). Without this, IOD invoices silently fell into
      // CASE 1 and picked up tariff.rate ("0.00" for imported codes), so every
      // line was R 0 regardless of the service's payment-options config.
      // Insurance is IOD-flagged but priced like private (flat rate per line),
      // so we exclude it from the medical-aid / MSR routing.
      const effectiveIsMedicalAid = isMedicalAid || (isIOD && !isInsurance);

      // Keep reference to original row (for patientId + appointment fields)
      const originalLine = lines[index] || {};

      // Prefer global default, else use line template, else none
      const templateForGroup =
        defaultAppointmentTemplate ||
        createDefaultTemplate(originalLine) ||
        null;

      const withTemplate = (line) =>
        templateForGroup ? { ...line, ...templateForGroup } : line;

      // Common base fields (appts carry from original, but template may override)
      const baseLineInfoCommon = {
        groupId,
        lineNumber: originalLine.lineNumber ?? index + 1,
        patientId: originalLine.patientId || selectedPatientId || "",
        useCustomDate: !!originalLine.useCustomDate,
        privatePayment: isEdiDisabled,

        appointmentId: originalLine.appointmentId || "",
        appointmentStartSet: originalLine.appointmentStartSet || null,
        appointmentEndSet: originalLine.appointmentEndSet || null,
        appointmentDateSet: originalLine.appointmentDateSet || null,
        appointmentCustomStart: originalLine.appointmentCustomStart || null,
        appointmentCustomEnd: originalLine.appointmentCustomEnd || null,
        appointmentDateCustom: originalLine.appointmentDateCustom || null,
        appointmentDate: originalLine.appointmentDate || null,

        icd101: primaryIcds[0] ? getIcdCode(primaryIcds[0]) : "",
        icd10Secondary: secondaryIcds.map(getIcdCode),
        serviceId,
        selectedService,
        type: "service",
      };

      // For year derivation: use invoiceDate fallback if needed
      const fallbackInvoiceDate =
        invoiceDate || new Date().toISOString().slice(0, 10);

      let newServiceLines = [];

      // ---------------------------
      // CASE 1:
      // tariffs exist AND (private OR (medical aid but useFlatRate && tariffs=0 ... your original logic))
      // NOTE: I kept your conditions intact, only added year handling where MSR is called.
      // Custom codes route here whenever they have tariffs (effectiveIsMedicalAid is false).
      // ---------------------------
      if (
        (!effectiveIsMedicalAid ||
          (effectiveIsMedicalAid && useFlatRate && tariffs?.length === 0)) &&
        tariffs?.length > 0
      ) {
        newServiceLines = tariffs.map((tariff, i) => {
          // For custom codes the source of truth for the line rate is
          // pricing.flatRate (the user-entered amount). Imported codes have
          // tariff.rate hard-coded to "0.00", so reading from the tariff
          // would always produce zero. Regular services keep their per-tariff
          // rate behaviour unchanged.
          const rawTariffRate = String(tariff.rate || "").replace(/[^0-9.]/g, "");
          const tariffRateNum = parseFloat(rawTariffRate) || 0;
          // Insurance accounts always bill the service's flat-rate amount
          // per line (same treatment as custom codes).
          const rate = (isCustomCode || isInsurance) ? fallbackFlatRate : tariffRateNum;

          const baseLine = withTemplate({
            ...baseLineInfoCommon,
            lineNumber: index + i + 1,
            tariffCode: tariff.code || "",
            description: descriptionFor(tariff.description),
            quantity: tariff.qty || 1,
            rate: rate.toFixed(2),
          });

          return applyModifiersToLine(baseLine, tariff.modifiers || []);
        });
      }

      // ---------------------------
      // CASE 2: Flat-rate only (no tariffs or private flow)
      // Custom codes without tariffs route here.
      // ---------------------------
      else if (
        !effectiveIsMedicalAid ||
        (effectiveIsMedicalAid && useFlatRate && tariffs?.length === 0)
      ) {
        const baseLine = withTemplate({
          ...baseLineInfoCommon,
          tariffCode: "",
          description: descriptionFor(),
          quantity: 1,
          rate: fallbackFlatRate.toFixed(2),
        });

        newServiceLines.push(applyModifiersToLine(baseLine));
      }

      // ---------------------------
      // CASE 3: Medical aid + useFlatRate + tariffs present (your existing logic)
      // Here you fetch MSR quantity, but rate is still tariff.rate
      // ✅ Year now derived from the *actual line* after template.
      // ---------------------------
      else if (effectiveIsMedicalAid && useFlatRate && tariffs?.length > 0) {
        newServiceLines = await Promise.all(
          tariffs.map(async (tariff, i) => {
            // Custom codes use pricing.flatRate as the rate (imported codes
            // have tariff.rate = 0). Description forced to "<code> - <desc>".
            const rawTariffRate = String(tariff.rate || "").replace(/[^0-9.]/g, "");
            const tariffRateNum = parseFloat(rawTariffRate) || 0;
            const lineRate = isCustomCode ? fallbackFlatRate : tariffRateNum;
            const baseLinePre = withTemplate({
              ...baseLineInfoCommon,
              lineNumber: index + i + 1,
              tariffCode: tariff.code || "",
              description: descriptionFor(tariff.description),
              quantity: tariff.qty || 1, // temp, overwritten by MSR quantity
              rate: lineRate.toFixed(2),
            });

            const lineDate = getLineServiceDate(
              baseLinePre,
              fallbackInvoiceDate,
            );
            const msrYear = getYearFromYYYYMMDD(lineDate);

            const msr = await getMSRRate(
              selectedPractice.practiceNumber,
              tariff.code,
              account.accountMedicalAidOptionCode,
              account.accountIODOptionCode,
              msrYear,
              selectedPractice.practiceContract,
            );

            const baseLine = {
              ...baseLinePre,
              quantity: msr.quantity,
            };

            return applyModifiersToLine(baseLine, tariff.modifiers || []);
          }),
        );
      }

      // ---------------------------
      // CASE 4: Medical aid, and MSR rate is used when useFlatAndMedicalAidRate
      // ✅ Year now derived from the actual line after template.
      // ---------------------------
      else {
        newServiceLines = await Promise.all(
          tariffs.map(async (tariff, i) => {
            let quantity = tariff.qty || 1;
            let invrate = 0;
            const description = descriptionFor(tariff.description);

            // Build the line FIRST (with template) so year is correct
            const baseLinePre = withTemplate({
              ...baseLineInfoCommon,
              lineNumber: index + i + 1,
              tariffCode: tariff.code || "",
              description,
              quantity,
              rate: "0.00", // set below
            });

            const lineDate = getLineServiceDate(
              baseLinePre,
              fallbackInvoiceDate,
            );
            const msrYear = getYearFromYYYYMMDD(lineDate);

            if (useFlatAndMedicalAidRate) {
              const msr = await getMSRRate(
                selectedPractice.practiceNumber,
                tariff.code,
                account.accountMedicalAidOptionCode,
                account.accountIODOptionCode,
                msrYear,
                selectedPractice.practiceContract,
              );
              invrate = Number(msr.rate || 0);
              quantity = msr.quantity;
            } else if (useFlatRate) {
              const rawRate = String(tariff.rate || "").replace(/[^0-9.]/g, "");
              invrate = parseFloat(rawRate) || 0;
            }

            const baseLine = {
              ...baseLinePre,
              quantity,
              originalRate: Number(invrate || 0).toFixed(2), // ✅ base before modifiers
              rate: Number(invrate || 0).toFixed(2),
            };

            const enriched = enrichModifiers(
              tariff.modifiers || [],
              modifierCatalog,
            );

            return applyModifiersToLine(baseLine, enriched);
          }),
        );
      }

      // ---------------------------
      // NAPPI lines (no modifiers)
      // ---------------------------
      const nappis = selectedService.pricing?.selectedNappi || [];
      if (nappis?.length) {
        const nappiLines = nappis.map((n, i) =>
          withTemplate({
            ...baseLineInfoCommon,
            type: "nappi",
            lineNumber: index + newServiceLines?.length + i + 1,
            selectedNappi: n,
            nappiCode: n.nappiCode || "",
            description: descriptionFor(n.name),
            quantity: 1,
            rate:
              typeof n.rate === "number"
                ? n.rate.toFixed(2)
                : String(n.rate ?? "0.00"),
          }),
        );

        newServiceLines = [...newServiceLines, ...nappiLines];
      }

      // Replace current line with the new group
      const updatedLines = [...lines];
      updatedLines.splice(index, 1, ...newServiceLines);
      setLines(updatedLines);

      // Set default template if we didn't have one yet
      if (!defaultAppointmentTemplate && newServiceLines[0]) {
        setDefaultAppointmentTemplate(
          createDefaultTemplate(newServiceLines[0]),
        );
      }
    },
    [
      services,
      customCodes,
      lines,
      account?.accountMedicalAid,
      account?.accountMedicalAidOptionCode,
      selectedPractice?.practiceNumber,
      selectedPatientId,
      defaultAppointmentTemplate,
      createDefaultTemplate,
      getMSRRate,
      primaryIcds,
      secondaryIcds,
      getIcdCode,
      invoiceDate,
      isEdiDisabled,
    ],
  );

  const handleTariffLineSelect = useCallback(
    async (index, tariff) => {
      if (!selectedPractice?.practiceNumber) return;

      const tariffCode = tariff.code;

      const fallbackInvoiceDate =
        invoiceDate || new Date().toISOString().slice(0, 10);

      // Derive year from THIS line’s appointment date (after any edits)
      const currentLine = lines[index] || {};
      const lineDate = getLineServiceDate(currentLine, fallbackInvoiceDate);
      const msrYear = getYearFromYYYYMMDD(lineDate);

      const msr = await getMSRRate(
        selectedPractice.practiceNumber,
        tariff.code,
        account.accountMedicalAidOptionCode,
        account.accountIODOptionCode,
        msrYear,
        selectedPractice.practiceContract,
      );

      setLines((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          selectedTariff: tariff,
          tariffCode,
          description: `${tariff.description} (${tariff.typeCode})`,
          quantity: msr.quantity,
          rate: (msr.rate ?? 0).toFixed(2),
        };
        return updated;
      });
    },
    [
      getMSRRate,
      selectedPractice?.practiceNumber,
      account.accountMedicalAidOptionCode,
      lines,
      invoiceDate,
    ],
  );

  const handleNappiLineSelect = useCallback((index, nappi) => {
    setLines((prev) => {
      const updated = [...prev];
      const rateRaw = nappi.rate ?? "0.00";
      const rate =
        typeof rateRaw === "number" ? rateRaw.toFixed(2) : String(rateRaw);
      updated[index] = {
        ...updated[index],
        selectedNappi: nappi,
        nappiCode: nappi.nappiCode || nappi.code || "",
        description: nappi.name || "",
        quantity: updated[index].quantity || 1,
        rate,
      };
      return updated;
    });
  }, []);

  const handleLineChange = useCallback(
    async (index, field, value) => {
      // Handle special async case
      if (field === "patientId") {
        const patientId = value;
        const selectedPatient = patients.find((p) => p._id === patientId);
        const appointments = await ensureAppointmentsForPatient(patientId);
        const firstAppointmentId = appointments[0]?._id || "";

        setLines((prev) => {
          const updated = [...prev];
          const currentLine = updated[index] || {};
          updated[index] = {
            ...currentLine,
            appointmentId: firstAppointmentId,
            patientFullNames: selectedPatient?.patientFirstName || "",
            patientLastName: selectedPatient?.patientLastName || "",
          };
          return updated;
        });
        return;
      }

      // General field update
      setLines((prev) => {
        const updated = [...prev];
        const currentLine = updated[index] || {};

        // Strip brackets from tariffCode - user cannot type them (modifiers add them as display only)
        let sanitizedValue = value;
        if (field === "tariffCode") {
          sanitizedValue = String(value || "").replace(/[()]/g, "");
        }

        let newLine = { ...currentLine, [field]: sanitizedValue };

        // If primary ICD changed, recompute oti
        if (field === "icd101") {
          const matchedICD = selectedICD10s.find(
            (icd) => getIcdCode(icd.item) === value,
          );
          newLine.oti = matchedICD?.isPMB ? "PB" : "A";
        }
        if (field === "rate") {
          newLine = {
            ...newLine,
            rateManuallyEdited: true, // ✅ user has overridden pricing
          };
        }

        updated[index] = newLine;
        console.log(`Line ${index} updated field ${field} to`, value);
        console.log("Updated line:", newLine);
        return updated;
      });
    },
    [patients, ensureAppointmentsForPatient, selectedICD10s],
  );

  const handleRemoveLine = useCallback((removeIndex) => {
    setLines((prevLines) =>
      prevLines
        .filter((_, i) => i !== removeIndex)
        .map((line, i) => ({ ...line, lineNumber: i + 1 })),
    );
  }, []);

  const getStatusVariant = useCallback((status) => {
    switch ((status || "").toLowerCase()) {
      case "draft":
        return "warning";
      case "pending":
        return "secondary";
      case "posted":
        return "success";
      case "paid":
        return "primary";
      case "claim in progress":
      case "posted - claim in progress":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "dark";
    }
  }, []);

  // ======= Invoice construction & persistence =======
  const constructInvoice = useCallback(
    (overrideStatus) => {
      const patientObj = patients.find((p) => p._id === selectedPatientId);
      const selectedPract = practitioners.find(
        (p) => p.id === selectedPractitionerId,
      );

      // Prefer ICDs from the invoice selections; if none, derive from attached case
      const icd10CodesForInvoice =
        icdSelections?.length > 0
          ? icdSelections.map((s) => ({
              ...s.item,
              isPrimary: !!s.isPrimary,
              useOnInvoice: !!s.useOnInvoice,
              isPMB: !!s.isPMB,
              pmbAuthNo: s.pmbAuthNo || "",
              pmbStartDate: s.pmbStartDate || "",
              pmbEndDate: s.pmbEndDate || "",
            }))
          : selectedICD10s?.length > 0
            ? selectedICD10s.map((s) => ({
                ...s.item,
                isPrimary: !!s.isPrimary,
                useOnInvoice: !!s.useOnInvoice,
                isPMB: !!s.isPMB,
                pmbAuthNo: s.pmbAuthNo || "",
                pmbStartDate: s.pmbStartDate || "",
                pmbEndDate: s.pmbEndDate || "",
              }))
            : attachedCase
              ? [
                  ...(attachedCase.icd101
                    ? [{ ICD10_Code: attachedCase.icd101, isPrimary: true }]
                    : []),
                  ...(attachedCase.icd102 || []).map((code) => ({
                    ICD10_Code: code,
                    isPrimary: false,
                  })),
                ]
              : [];
      console.log("ICD10 Codes for Invoice:", icd10CodesForInvoice);
      // Build a compatible inPatientCase block (legacy consumers) from attachedCase when present
      const inPatientCaseCompat = attachedCase
        ? {
            caseAuthNumber: attachedCase.authorizationNumber || "",
            caseBHF: attachedCase.referringDoctorBHF || "",
            caseHPCSA: attachedCase.referringDoctorHPCSA || "",
            caseICD10: attachedCase.icd101 || "",
          }
        : null;

      // Optional rich case block to persist full case details with the invoice
      const caseBlock = attachedCase
        ? {
            id: attachedCase._id || null,
            description: attachedCase.caseDescription || "",
            referringDoctorName: attachedCase.referringDoctorName || "",
            referringDoctorBHF: attachedCase.referringDoctorBHF || "",
            referringDoctorHPCSA: attachedCase.referringDoctorHPCSA || "",
            authorizedBy: attachedCase.authorizedBy || "",
            authorizationNumber: attachedCase.authorizationNumber || "",
            startDate: attachedCase.caseStartDate || "", // MM-DD-YYYY
            endDate: attachedCase.caseEndDate || "", // MM-DD-YYYY
            icd101: attachedCase.icd101 || "",
            icd102: Array.isArray(attachedCase.icd102)
              ? attachedCase.icd102
              : [],
          }
        : null;

      const newInvoice = {
        accountId: account._id,
        accountName: `${account.accountFullNames} ${account.accountLastName}`,
        accountEmail: account.accountEmail,
        medicalAid: {
          name: account.accountMedicalAid,
          plan: account.accountMedicalAidPlan,
          option: account.accountMedicalAidOption,
          number: account.accountMedicalAidNumber,
          optionCode: account.accountMedicalAidOptionCode,
        },
        practice: selectedPractice
          ? {
              practiceNumber: selectedPractice.practiceNumber,
              practiceName: selectedPractice.practiceName,
              vatNumber: selectedPractice.vatNumber,
            }
          : null,
        practitioner: selectedPract
          ? {
              id: selectedPractitionerId,
              firstName: selectedPract.firstName,
              lastName: selectedPract.lastName,
              hpcsa: selectedPract.hpcsa,
            }
          : null,
        invoiceNumber: invoiceNumber || null,
        invoiceDate,
        totalAmount,
        totalMedicalAidAmount,
        medAidTotal: totalMedicalAidAmount,
        totalPatientAmount,
        patientTotal: totalPatientAmount,
        placeOfService: selectedPlaceOfService,
        outPatient: outpatient,

        // NEW: persist selected case cleanly
        caseId: attachedCase ? attachedCase._id || null : null,
        case: caseBlock,

        // Keep legacy block (mapped from selected case when present)
        inPatientCase: inPatientCaseCompat,

        // ICDs for the invoice header
        icd10Codes: icd10CodesForInvoice,

        patient: {
          patientId: patientObj?._id,
          patientFirstName: patientObj?.patientFirstName,
          patientLastName: patientObj?.patientLastName,
          patientMedicalAid: patientObj?.patientMedicalAid,
          patientMedicalAidNumber: patientObj?.patientMedicalAidNumber,
          patientMedicalAidOptionCode: patientObj?.patientMedicalAidOptionCode,
          patientMedicalAidPlanCode: patientObj?.patientMedicalAidPlanCode,
          patientMedicalAidDependantCode:
            patientObj?.patientMedicalAidDependantCode,
        },

        outstandingBalance: {
          patientOutstanding: totalPatientAmount,
          medAidOutstanding: totalMedicalAidAmount,
          totalOutstanding: totalAmount,
          paidAmount: 0,
        },

        lines: lines?.map((line) => {
          const full =
            Math.round(
              (parseFloat(String(line.rate || "0").replace(/[, ]+/g, "")) ||
                0) * 100,
            ) / 100;
          let patientAmount = 0;
          let medAidAmount = 0;
          const hasBillableCode = Boolean(line.tariffCode || line.nappiCode);

          if (
            (!account.accountMedicalAid && !account.accountIODFund) ||
            line.privatePayment ||
            !hasBillableCode
          ) {
            patientAmount = full;
          } else {
            medAidAmount = full;
          }

          // Recompute oti per line, based on that line’s icd101
          const matchedICD = icd10CodesForInvoice.find(
            (icd) => icd.ICD10_Code === line.icd101 && icd.isPMB,
          );
          const lineOti = matchedICD ? "PB" : "A";

          return {
            lineNumber: line.lineNumber,
            type: line.type,
            serviceId: line.serviceId || null,
            selectedService: line.selectedService || null,
            selectedTariff: line.selectedTariff || null,
            selectedNappi: line.selectedNappi || null,
            oti: lineOti,
            icd101: line.icd101,
            icd102:
              Array.isArray(line.icd10Secondary) && line.icd10Secondary?.length
                ? line.icd10Secondary.join(";")
                : line.icd102 || "",
            icd10Secondary: Array.isArray(line.icd10Secondary)
              ? line.icd10Secondary
              : [],
            tariffCode: line.tariffCode,
            nappiCode: line.nappiCode || null,
            modifier: line.modifier || null,
            serviceProvider: line.serviceProvider || null,
            originalRate: line.originalRate || null,
            description: line.description,
            quantity: line.quantity,
            rate: full,
            privatePayment: line.privatePayment || false,
            patientAmount,
            medAidAmount,
            appointmentDateSet: line.useCustomDate
              ? null
              : line.appointmentStartSet
                ? line.appointmentStartSet.slice(0, 10)
                : null,
            appointmentStartSet: line.useCustomDate
              ? null
              : line.appointmentStartSet || null,
            appointmentEndSet: line.useCustomDate
              ? null
              : line.appointmentEndSet || null,
            appointmentDateCustom: line.useCustomDate
              ? line.appointmentCustomStart
                ? line.appointmentCustomStart.slice(0, 10)
                : line.appointmentDate || null
              : null,
            appointmentCustomStart: line.useCustomDate
              ? line.appointmentCustomStart || null
              : null,
            appointmentCustomEnd: line.useCustomDate
              ? line.appointmentCustomEnd || null
              : null,
            appointmentId: line.appointmentId || null,
          };
        }),
      };

      if (overrideStatus) newInvoice.status = overrideStatus;

      setInvoice(newInvoice);
      console.log("New invoice created:", newInvoice);
      return newInvoice;
    },
    [
      // deps
      account._id,
      account.accountFullNames,
      account.accountLastName,
      account.accountEmail,
      account.accountMedicalAid,
      account.accountIODFund,
      account.accountMedicalAidPlan,
      account.accountMedicalAidOption,
      account.accountMedicalAidNumber,
      account.accountMedicalAidOptionCode,
      icdSelections,
      selectedICD10s,
      attachedCase,
      invoiceNumber,
      invoiceDate,
      totalAmount,
      totalMedicalAidAmount,
      totalPatientAmount,
      selectedPlaceOfService,
      outpatient,
      lines,
      patients,
      practitioners,
      selectedPatientId,
      selectedPractitionerId,
      selectedPractice,
    ],
  );

  const refreshCases = useCallback(async (pid) => {
    if (!pid) return [];
    const resp = await getCasesByPatientId(pid);
    // getCasesByPatientId returns the raw axios response in some paths;
    // unwrap defensively the same way CasePickerPanel/CaseModal do.
    const list = Array.isArray(resp)
      ? resp
      : Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.cases)
          ? resp.cases
          : Array.isArray(resp?.items)
            ? resp.items
            : [];
    setCases(list);
    return list;
  }, []);

  const filterRelevantCases = useCallback((allCases) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upperCutoff = new Date(today);
    upperCutoff.setDate(upperCutoff.getDate() + 30);
    const lowerCutoff = new Date(today);
    lowerCutoff.setDate(lowerCutoff.getDate() - 30);
    return (allCases || []).filter((c) => {
      const end = normalizeToMMDDYYYY(c?.caseEndDate);
      if (!end) return true;
      const [mm, dd, yyyy] = end.split("-");
      if (!mm || !dd || !yyyy) return true;
      const endDate = new Date(`${yyyy}-${mm}-${dd}`);
      if (Number.isNaN(endDate.getTime())) return true;
      // Include cases ending within ±30 days (supports back-dated invoices)
      return endDate >= lowerCutoff && endDate <= upperCutoff;
    });
  }, []);

  // call once on mount / patient change — and auto-prompt if relevant cases exist
  useEffect(() => {
    if (!selectedPatientId) return;
    let cancelled = false;
    (async () => {
      const list = await refreshCases(selectedPatientId);
      if (cancelled) return;
      if (attachedCase) return;
      const matches = filterRelevantCases(list);
      if (matches.length > 0) {
        setSoonExpiringCases(matches);
        setShowSoonExpiringPrompt(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPatientId, refreshCases, attachedCase, filterRelevantCases]);

  const handleAttachCase = useCallback(
    async (c) => {
      setAttachedCase(c);
      await refreshCases(selectedPatientId);
      setShowAddCase(false);
      setShowSoonExpiringPrompt(false);

      let nextSelections = [];
      if (Array.isArray(c.icdSelections) && c.icdSelections?.length) {
        nextSelections = c.icdSelections.map((s) => ({
          item: s.item ?? s,
          isPrimary: !!s.isPrimary,
        }));
      } else {
        const primaryItem =
          c.icd101Full || (c.icd101 ? { ICD10_Code: c.icd101 } : null);
        const secondaryItems =
          Array.isArray(c.icd102Full) && c.icd102Full?.length
            ? c.icd102Full
            : Array.isArray(c.icd102)
              ? c.icd102.map((code) => ({ ICD10_Code: code }))
              : [];
        nextSelections = [
          ...(primaryItem ? [{ item: primaryItem, isPrimary: true }] : []),
          ...secondaryItems.map((item) => ({ item, isPrimary: false })),
        ];
      }

      if (nextSelections?.length) {
        setIcdSelections(nextSelections);
        setSelectedICD10s(nextSelections.map((s) => s.item));
        setLines((prev) =>
          prev.map((l) =>
            !l.icd101 && c.icd101 ? { ...l, icd101: c.icd101 } : l,
          ),
        );
      }
    },
    [selectedPatientId, refreshCases],
  );

  // Fetch the linked appointment when we arrived via the appointment flow,
  // so we know its current status (to hide the checkbox once Completed).
  useEffect(() => {
    if (!flowAppointmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const appt = await getAppointmentById(flowAppointmentId);
        if (!cancelled) setFlowAppointment(appt || null);
      } catch (err) {
        console.error("Failed to load flow appointment:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flowAppointmentId]);

  // Opt-in completion of the linked appointment for the
  // appointment → FollowUpSession → invoice flow. No-op unless we arrived
  // with an appointmentId, the checkbox is ticked, and it isn't already done.
  const maybeCompleteFlowAppointment = useCallback(async () => {
    if (
      !completeAppointmentChecked ||
      !flowAppointmentId ||
      flowAppointment?.status === "Completed" ||
      // "New Patient" → "Completed" is blocked server-side.
      flowAppointment?.status === "New Patient"
    ) {
      return;
    }
    try {
      await updateAppointmentStatusByPatientId(flowAppointmentId, "Completed");
    } catch (statusErr) {
      console.error("Failed to mark appointment complete:", statusErr);
    }
  }, [completeAppointmentChecked, flowAppointmentId, flowAppointment]);

  const saveNewInvoice = useCallback(
    async (onStart, onEnd) => {
      if (!validateInvoiceFields() || !validateInvoiceLines()) return;
      onStart?.("saveNew");
      try {
        const invoiceToSave = constructInvoice("Pending");
        const response = await createInvoice(invoiceToSave);
        const newNumber =
          response.invoiceNumber ?? response.invoice?.invoiceNumber;
        const newStatus = response.status ?? response.invoice?.status;

        setInvoiceNumber(newNumber);
        setInvoiceStatus(newStatus || "Pending");

        await maybeCompleteFlowAppointment();

        showToast("success", "Invoice saved successfully!");

        setTimeout(() => {
          if (window.innerWidth <= 933) {
            navigate(getBookingsReturnPath());
          } else {
            navigate(`/accounts/${account._id}`);
          }
        }, 3000);
      } finally {
        onEnd?.();
      }
    },
    [
      validateInvoiceFields,
      validateInvoiceLines,
      constructInvoice,
      navigate,
      account._id,
      maybeCompleteFlowAppointment,
    ],
  );

  const saveAndPostNewInvoice = useCallback(
    async (onStart, onEnd) => {
      if (!validateInvoiceFields() || !validateInvoiceLines()) return;
      onStart?.("savePostNew");
      try {
        const invoiceToSave = constructInvoice();
        const hasMedicalAid =
          (!!invoiceToSave.medicalAid?.name || !!account.accountIODFund) &&
          invoiceToSave.lines.some((line) => !line.privatePayment);

        invoiceToSave.status = hasMedicalAid
          ? "Posted - Claim in Progress"
          : "Posted";
        const response = await createInvoice(invoiceToSave);
        const newNumber =
          response.invoiceNumber ?? response.invoice?.invoiceNumber;
        const newStatus = response.status ?? response.invoice?.status;
        const responseId = response.invoiceId || response.invoice?._id;

        setInvoiceNumber(newNumber);
        setInvoiceStatus(newStatus || "Posted");

        await maybeCompleteFlowAppointment();

        showToast("success", "Invoice posted successfully!");

        setTimeout(() => {
          if (window.innerWidth <= 933) {
            navigate(getBookingsReturnPath());
          } else {
            navigate(`/invoices/posted/${responseId}`);
          }
        }, 3000);
      } finally {
        onEnd?.();
      }
    },
    [
      validateInvoiceFields,
      validateInvoiceLines,
      constructInvoice,
      navigate,
      account.accountIODFund,
      showToast,
      maybeCompleteFlowAppointment,
    ],
  );

  const updateExistingInvoiceAndClaim = useCallback(
    async (onStart, onEnd) => {
      console.log("validateInvoiceFields:", validateInvoiceFields());
      console.log("validateInvoiceLines:", validateInvoiceLines());
      if (!validateInvoiceFields() || !validateInvoiceLines()) return;
      onStart?.("update");
      try {
        const invoiceToSave = constructInvoice();
        const claim = await getClaimByInvoiceId(invoice._id);
        console.log("🔍 Fetched claim for invoice:", claim);
        const hasMedicalAid =
          (!!invoiceToSave.medicalAid?.name || !!account.accountIODFund) &&
          invoiceToSave.lines.some((line) => !line.privatePayment);

        const updatedInvoice = {
          ...invoice,
          status:
            hasMedicalAid && feedback?._id && invoice.status !== "Pending"
              ? "Posted - Claim in Progress"
              : hasMedicalAid && invoice.status === "Posted"
                ? "Posted"
                : invoice.status,
        };

        setInvoice(updatedInvoice);

        if (hasMedicalAid && claim?._id) {
          const updatedClaim = {
            ...claim,
            status: "Claim Updated",
            lineResults: [],
          };

          await updateClaim(claim._id, updatedClaim);
        }

        await updateInvoice(invoice._id, {
          ...invoiceToSave,
          status: updatedInvoice.status,
        });

        showToast("success", "Invoice updated successfully!");

        setTimeout(() => {
          if (invoice.status === "Pending") {
            navigate(`/accounts/${account._id}`);
          } else {
            navigate(`/invoices/posted/${invoice._id}`);
          }
        }, 3000);
      } finally {
        onEnd?.();
      }
    },
    [
      validateInvoiceFields,
      validateInvoiceLines,
      constructInvoice,
      invoice,
      feedback,
      navigate,
      account._id,
      account.accountIODFund,
      showToast,
    ],
  );

  const handlePost = useCallback(
    async (onStart, onEnd) => {
      onStart?.("post");
      try {
        const invoiceToSave = constructInvoice();

        const hasMedicalAid =
          (!!invoiceToSave.medicalAid?.name || !!account.accountIODFund) &&
          invoiceToSave.lines.some((line) => !line.privatePayment);

        const updatedStatus = hasMedicalAid
          ? "Posted - Claim in Progress"
          : "Posted";

        const updatedInvoice = {
          ...invoiceToSave,
          status: updatedStatus,
        };

        const response = await updateInvoice(invoice._id, updatedInvoice);

        setInvoiceStatus(updatedStatus);

        if (typeof onInvoiceUpdated === "function") {
          onInvoiceUpdated();
        }

        if (response) {
          showToast("success", "Invoice posted successfully!");
          setTimeout(() => {
            navigate(`/invoices/posted/${invoice._id}`);
          }, 3000);
        }
      } catch (error) {
        console.error("❌ Error posting invoice:", error);
      } finally {
        onEnd?.();
      }
    },
    [
      constructInvoice,
      invoice?._id,
      onInvoiceUpdated,
      navigate,
      account.accountIODFund,
      showToast,
    ],
  );

  const handleDeleteInvoice = useCallback(async () => {
    setBusy("delete");
    try {
      await deleteInvoice(invoice._id);
      setShowDeleteModal(false);
      if (typeof onInvoiceUpdated === "function") {
        onInvoiceUpdated();
      }
      showToast("success", "Invoice deleted successfully!");
      setTimeout(() => {
        navigate(`/accounts/${account._id}`);
      }, 2000);
    } catch (error) {
      console.error("❌ Error deleting invoice:", error);
      alert(error.response?.data?.error || "Failed to delete invoice");
    } finally {
      setBusy(null);
    }
  }, [invoice?._id, onInvoiceUpdated, navigate, account._id, showToast]);

  const selectedPract = useMemo(
    () => practitioners.find((p) => p.id === selectedPractitionerId),
    [practitioners, selectedPractitionerId],
  );

  // Use whatever field your user object actually has:

  // ======= Effects =======
  // Handle nav state incoming invoice
  useEffect(() => {
    if (!practice?.length) return;
    // Handle navigation from appointment screen
    if (location.state?.appointmentId) {
      const {
        patientId,
        practitionerId,
        practiceId,
        appointmentId,
        appointmentStart,
        appointmentEnd,
      } = location.state;

      // Set patient & practitioner
      if (patientId) setSelectedPatientId(patientId);
      if (practitionerId) setSelectedPractitionerId(practitionerId);
      // Match practice
      if (practiceId) {
        const matchedPractice = practice.find(
          (p) => p.practiceNumber === practiceId,
        );
        if (matchedPractice) {
          setSelectedPracticeId(practiceId);
          console.log("practiceId from nav state:", practiceId);
          setSelectedPractice(matchedPractice);
          console.log("Matched practice from nav state:", matchedPractice);
        }
      }

      // Only update lines after they exist
      if (lines?.length > 0) {
        const updatedLines = lines.map((line) => ({
          ...line,
          useCustomDate: false,
          appointmentId,
          appointmentStartSet: appointmentStart || null,
          appointmentEndSet: appointmentEnd || null,
          appointmentDateSet: appointmentStart
            ? appointmentStart.slice(0, 10)
            : null,
          appointmentDate: appointmentStart
            ? appointmentStart.slice(0, 10)
            : null,
          appointmentCustomStart: null,
          appointmentCustomEnd: null,
          appointmentDateCustom: null,
          rateManuallyEdited: false,
        }));

        setLines(updatedLines);
        setLineApptInitial({
          useCustomDate: false,
          appointmentId,
          appointmentStartSet: appointmentStart || null,
          appointmentEndSet: appointmentEnd || null,
          appointmentDateSet: appointmentStart
            ? appointmentStart.slice(0, 10)
            : null,
          appointmentDate: appointmentStart
            ? appointmentStart.slice(0, 10)
            : null,
          appointmentCustomStart: null,
          appointmentCustomEnd: null,
          appointmentDateCustom: null,
        });
      }

      return;
    }
  }, [location.state, lines?.length, practice, initialInvoiceApp]);

  const disciplineCode =
    selectedPract?.disciplineCode || // <-- if your practitioner has this
    selectedPract?.practitionerTypeCode || // <-- or this
    selectedPractice?.practiceDisciplineCode ||
    initialDisciplineCode || // fallback
    null;

  // Update icd primaries on change
  useEffect(() => {
    setLines((prev) =>
      prev.map((l) => {
        if (!l.icd101) return l;
        const stillPrimary = primaryIcds.some(
          (icd) => getIcdCode(icd) === l.icd101,
        );
        if (stillPrimary || primaryIcds?.length === 0) return l;
        return { ...l, icd101: getIcdCode(primaryIcds[0]) };
      }),
    );
  }, [primaryIcds, getIcdCode]);

  // Load patients + services
  useEffect(() => {
    async function loadPatientsAndServices() {
      // Both API helpers return null on a non-200 / error (e.g. a 403 from the
      // tenant check). Coerce to [] so the .map() render paths never see null.
      const patientData = await getAccountPatients(account._id);
      setPatients(Array.isArray(patientData) ? patientData : []);

      // accountPractice is inconsistently stored — sometimes an array,
      // sometimes a bare string. Coerce so the route's array guard passes.
      const practiceList = Array.isArray(account.accountPractice)
        ? account.accountPractice
        : account.accountPractice
          ? [account.accountPractice]
          : [];
      const servicesData = await getServicesByPracticeNumbers(practiceList);
      setServices(Array.isArray(servicesData) ? servicesData : []);
      // Custom codes live in the same collection but are filtered out of the
      // default fetch (see backend serviceTypeMongoFilter). Opt in explicitly.
      const customCodesData = await getServicesByPracticeNumbers(practiceList, {
        serviceType: "custom_code",
      });
      setCustomCodes(Array.isArray(customCodesData) ? customCodesData : []);
      console.log("Loaded services for account practices:", servicesData);
    }
    loadPatientsAndServices();
  }, [account]);

  useEffect(() => {
    if (!selectedPatientId) return;
    if (icdSelections?.length > 0) return; // ICDs already selected manually or from loaded invoice

    async function fetchInvoiceICDs() {
      const data = await getPatientInvoiceICDs(selectedPatientId);

      if (!Array.isArray(data) || data?.length === 0) return;

      setSelectedICD10s(data);
      setIcdSelections(
        data.map((s) => ({
          item: s.item ?? s,
          isPrimary: !!s.isPrimary,
          isPMB: !!s.isPMB,
          pmbAuthNo: s.pmbAuthNo || "",
          pmbStartDate: s.pmbStartDate || "",
          pmbEndDate: s.pmbEndDate || "",
          useOnInvoice: true,
        })),
      );

      // Update lines: inject first primary ICD10
      const firstPrimary = data.find((s) => s.isPrimary);
      if (firstPrimary?.item) {
        const code =
          firstPrimary.item.ICD10_Code || firstPrimary.item.ICD10_3_Code || "";
        setLines((prev) =>
          prev.map((line) => (!line.icd101 ? { ...line, icd101: code } : line)),
        );
      }
    }
    console.log("ICD Selections:", icdSelections);
    fetchInvoiceICDs();
  }, [selectedPatientId, icdSelections]);

  // Defaults for practice, practitioner, POS
  useEffect(() => {
    if (practice?.length && !selectedPracticeId) {
      const firstPractice = practice[0];
      setSelectedPracticeId(firstPractice.practiceNumber);
      setSelectedPractice(firstPractice); // ✅ Also set the object
      console.log(
        "Defaulting to first practice:",
        firstPractice.practiceNumber,
      );
    }
  }, [practice]);

  // Sync selectedPractice with full practice data when loading existing invoice
  // This ensures practiceDisciplineCode is available for modifiers
  useEffect(() => {
    if (
      practice?.length &&
      selectedPracticeId &&
      !selectedPractice?.practiceDisciplineCode
    ) {
      const fullPractice = practice.find(
        (p) => p.practiceNumber === selectedPracticeId,
      );
      if (fullPractice?.practiceDisciplineCode) {
        console.log(
          "Syncing selectedPractice with full practice data:",
          fullPractice.practiceNumber,
          fullPractice.practiceDisciplineCode,
        );
        setSelectedPractice(fullPractice);
        setInitialDisciplineCode(fullPractice.practiceDisciplineCode);
      }
    }
  }, [practice, selectedPracticeId, selectedPractice?.practiceDisciplineCode]);

  useEffect(() => {
    const disc = disciplineCode;
    if (!disc) {
      setModifierCatalog(new Map());
      return;
    }

    (async () => {
      try {
        const mods = await getModifierByDisciplineCode(disc);
        const map = new Map();
        (mods || []).forEach((m) => {
          if (!m?.code) return;
          map.set(String(m.code), {
            adjustment: m.adjustment,
            adjustmentPercentage: m.adjustmentPercentage,
            description: m.description,
          });
        });
        setModifierCatalog(map);
      } catch (e) {
        console.error("Failed to load modifier catalog:", e);
        setModifierCatalog(new Map());
      }
    })();
  }, [disciplineCode]);

  // Only pick first POS if none set
  useEffect(() => {
    if (placesOfService?.length && !selectedPlaceOfService) {
      const first = placesOfService[0];
      setSelectedPlaceOfService(first.HCFA_code);
      setOutpatient(first.out ? "O" : "I");
    }
  }, [placesOfService, selectedPlaceOfService]);

  // Load practitioners on practice change
  useEffect(() => {
    if (!selectedPracticeId) return;

    async function getPractitioners() {
      const data = await fetchallUsersByPractices([selectedPracticeId]);
      console.log("Fetched users for practice:", data);
      // ✅ Only keep users whose role is exactly "Practitioner"
      const practitionersOnly = (data || []).filter((u) => {
        if (Array.isArray(u.role)) {
          return u.role.includes("Practitioner");
        }
        return u.role === "Practitioner";
      });

      setPractioners(practitionersOnly);

      // ✅ Set default only if it's a Practitioner
      if (!selectedPractitionerId && practitionersOnly?.length > 0) {
        setSelectedPractitionerId(practitionersOnly[0].id);
      }

      console.log("Loaded practitioners for practice:", practitionersOnly);
    }

    getPractitioners();
  }, [selectedPracticeId]);

  // Separate effect: Set initial practitioner (only runs once on mount or when initial data loads)
  useEffect(() => {
    // Skip if we already have a practitioner selected
    if (selectedPractitionerId) return;

    // Skip if we don't have practitioners loaded yet
    if (!practitioners?.length) return;

    const desiredId =
      initialInvoice?.practitioner?.id || initialInvoiceApp?.practitioner?.id;
    console.log("Setting initial practitioner ID:", desiredId);

    if (desiredId && practitioners?.some((p) => p.id === desiredId)) {
      setSelectedPractitionerId(desiredId);
    } else {
      // Fall back to first practitioner
      setSelectedPractitionerId(practitioners[0]?.id);
    }
  }, [
    practitioners,
    initialInvoice?.practitioner?.id,
    initialInvoiceApp?.practitioner?.id,
    selectedPractitionerId,
  ]);

  // Apply initialInvoice structure and fetch appts for that patient (once)
  useEffect(() => {
    if (initialInvoiceApp || !id) return;
    let isMounted = true;
    (async () => {
      try {
        const loaded = await getInvoiceById(id);
        console.log("🚚 Loaded invoice by ID:", loaded);
        if (!isMounted || !loaded) return;

        // Basic invoice fields
        setInvoice(loaded);
        setInvoiceDate(
          loaded.invoiceDate || new Date().toISOString().split("T")[0],
        );
        setInvoiceNumber(loaded.invoiceNumber || "");
        setInvoiceStatus(loaded.status || "Draft");
        setSelectedPracticeId(loaded.practice?.practiceNumber || "");
        console.log(
          "Setting selected practice ID from loaded invoice:",
          loaded.practice?.practiceNumber,
        );
        setInitialDisciplineCode(loaded.practice?.practiceDisciplineCode || "");
        console.log(
          "Setting initial discipline code from loaded invoice:",
          loaded.practice?.practiceDisciplineCode,
        );
        setSelectedPractice(loaded.practice || null);
        setSelectedPatientId(loaded.patient?.patientId || "");
        setSelectedPractitionerId(loaded.practitioner?.id || "");
        setSelectedPlaceOfService(loaded.placeOfService || "");
        setInPatientCase(
          loaded.inPatientCase || {
            caseAuthNumber: "",
            caseBHF: "",
            caseHPCSA: "",
            caseICD10: "",
          },
        );

        // ✅ Handle both appointmentDate and appointmentDateCustom at root level
        if (loaded.appointmentDateCustom) {
          setAppointmentDate(loaded.appointmentDateCustom);
        } else if (loaded.appointmentDate) {
          setAppointmentDate(loaded.appointmentDate);
        } else {
          setAppointmentDate("");
        }

        setOutpatient(loaded.outPatient !== "I");
        setLines(loaded.lines || []);
        setSelectedICD10s(loaded.icd10Codes || []);

        // ✅ Respect isPrimary from Mongo
        if (Array.isArray(loaded.icd10Codes)) {
          const seeded = loaded.icd10Codes.map((x) => ({
            item: x,
            isPrimary: !!x.isPrimary,
            isPMB: !!x.isPMB,
            pmbAuthNo: x.pmbAuthNo || "",
            pmbStartDate: x.pmbStartDate || "",
            pmbEndDate: x.pmbEndDate || "",
          }));
          setIcdSelections(seeded);
          console.log("ICD selections from loaded invoice:", seeded);
        }

        // ✅ Update each line’s appointment/custom date fields
        if (loaded.lines?.length > 0) {
          const transformedLines = loaded.lines.map((line) => {
            const useCustomDate = !!line.appointmentDateCustom;
            return {
              ...line,
              patientId: line.patientId || loaded.patient?.patientId || "", // ✅ ensure every line has it
              useCustomDate,
              appointmentDate: useCustomDate
                ? line.appointmentDateCustom || ""
                : line.appointmentDateSet || "",
              appointmentStartSet: line.appointmentStartSet || null,
              appointmentEndSet: line.appointmentEndSet || null,
              appointmentCustomStart: line.appointmentCustomStart || null,
              appointmentCustomEnd: line.appointmentCustomEnd || null,
              appointmentDateCustom: line.appointmentDateCustom || null,
              rateManuallyEdited: false,
            };
          });

          setLines(transformedLines);
          setDefaultAppointmentTemplate(
            createDefaultTemplate(transformedLines[0]),
          );
          setLineApptInitial(transformedLines[0]);
        }

        setAttachedCase(loaded.case || null);
      } catch (e) {
        console.error("Failed to load invoice by id:", e);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [initialInvoiceApp, id, createDefaultTemplate]);

  // Preload first patient appointments (cached)
  useEffect(() => {
    async function preloadFirstPatientAppointments() {
      if (patients?.length && !lineAppointments[patients[0]._id]) {
        const data = await getAppointmentsByPatientId(patients[0]._id);
        setLineAppointments((prev) => ({
          ...prev,
          [patients[0]._id]: data,
        }));
      }
    }
    preloadFirstPatientAppointments();
  }, [patients, lineAppointments]);

  // Populate default service line coming from navigation
  useEffect(() => {
    const populateDefaultServiceLine = async () => {
      if (incomingServiceId && services?.length > 0 && lines?.length === 0) {
        await handleServiceSelect(0, incomingServiceId);

        // After service is added, apply appointment data to ALL lines if available
        if (location.state?.appointmentId) {
          const { appointmentId, appointmentStart, appointmentEnd } =
            location.state;

          setLines((prev) =>
            prev.map((line) => ({
              ...line,
              useCustomDate: false,
              appointmentId,
              appointmentStartSet: appointmentStart || null,
              appointmentEndSet: appointmentEnd || null,
              appointmentDateSet: appointmentStart
                ? appointmentStart.slice(0, 10)
                : null,
              appointmentDate: appointmentStart
                ? appointmentStart.slice(0, 10)
                : null,
            })),
          );
        }
      }
    };
    populateDefaultServiceLine();
  }, [
    incomingServiceId,
    services,
    lines?.length,
    handleServiceSelect,
    location.state,
  ]);

  // ======= Render =======

  const renderPracticeSection = () => (
    <>
      <Form.Group as={Row} className="mb-2 gx-2 align-items-center">
        <Form.Label
          column
          xs={4}
          className="fw-semibold text-primary small mb-0"
        >
          Practice:
        </Form.Label>
        <Col xs={8}>
          <Form.Select
            size="sm"
            value={selectedPracticeId || ""}
            onChange={(e) => {
              const selected = practice.find(
                (p) => p.practiceNumber === e.target.value,
              );
              setSelectedPracticeId(e.target.value);
              console.log("Selected practice ID:", e.target.value);
              console.log("Selected practice object:", selected);
              setSelectedPractice(selected);
            }}
            isInvalid={!!invoiceErrors.practice} // ✅ Added this
          >
            <option value="">Select a practice...</option>
            {practice?.map((p) => (
              <option key={p.practiceNumber} value={p.practiceNumber}>
                {p.practiceName}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {invoiceErrors.practice}
          </Form.Control.Feedback>
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-2 gx-2 align-items-center">
        <Form.Label
          column
          xs={4}
          className="fw-semibold text-primary small mb-0"
        >
          Practitioner:
        </Form.Label>
        <Col xs={8}>
          <Form.Select
            size="sm"
            value={selectedPractitionerId || ""}
            onChange={(e) => setSelectedPractitionerId(e.target.value || "")}
            isInvalid={!!invoiceErrors.practitioner}
          >
            <option value="">Select a practitioner...</option>
            {practitioners
              .filter((p) => p.role?.includes("Practitioner"))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.firstName} {p.lastName}
                </option>
              ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {invoiceErrors.practitioner}
          </Form.Control.Feedback>
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-1 gx-2 align-items-center">
        <Form.Label
          column
          xs={4}
          className="fw-semibold text-primary small mb-0"
        >
          POS:
        </Form.Label>
        <Col xs={8}>
        <Form.Select
          size="sm"
          value={selectedPlaceOfService}
          onChange={(e) => {
            const selectedCode = e.target.value;
            const selectedPOS = placesOfService.find(
              (pos) => pos.HCFA_code === selectedCode,
            );
            setSelectedPlaceOfService(selectedCode);
            if (selectedPOS) {
              setOutpatient(selectedPOS.out ? "O" : "I");
            }
          }}
          isInvalid={!!invoiceErrors.placeOfService}
        >
          <option value="">Select a place of service...</option>
          {placesOfService.map((pos) => (
            <option key={pos.HCFA_code} value={pos.HCFA_code}>
              {pos.HCFA_code} - {pos.newDescription}
            </option>
          ))}
        </Form.Select>
          <Form.Control.Feedback type="invalid">
            {invoiceErrors.placeOfService}
          </Form.Control.Feedback>
          {hasPlaceOfServiceConflict && (
            <div className="text-warning small mt-1">
              Conflicting Places of Service on the selected appointments.
            </div>
          )}
        </Col>
      </Form.Group>
    </>
  );

  const pad = (n) => String(n).padStart(2, "0");
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return iso; // already a date-only string?
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  function deriveCaseView(c) {
    if (!c) return null;

    // Try icd from selections first; otherwise fall back to fields.
    let primaryICD = c.icd101 || null;
    let secondaryICDs = Array.isArray(c.icd102) ? c.icd102 : [];

    if (Array.isArray(c.icdSelections) && c.icdSelections?.length) {
      const prim = c.icdSelections.find((s) => s.isPrimary);
      if (prim?.item?.ICD10_Code) primaryICD = prim.item.ICD10_Code;
      const secs = c.icdSelections
        .filter((s) => !s.isPrimary && s?.item?.ICD10_Code)
        .map((s) => s.item.ICD10_Code);
      if (secs?.length) secondaryICDs = secs;
    }

    // Referrer could be flat or nested
    const refName =
      c.referringDoctorName || c.referrer?.name || c.referrerName || "—";
    const refBHF =
      c.referringDoctorBHF || c.referrer?.bhf || c.referrerBHF || "—";
    const refHPCSA =
      c.referringDoctorHPCSA || c.referrer?.hpcsa || c.referrerHPCSA || "—";

    return {
      title:
        (c.caseDescription && c.caseDescription.trim()) ||
        c.title ||
        c.caseName ||
        "Untitled Case",
      start: fmtDate(c.caseStartDate || c.startDate || c.start),
      end:
        c.caseEndDate || c.endDate || c.end
          ? fmtDate(c.caseEndDate || c.endDate || c.end)
          : "",
      authorizedBy: c.authorizedBy || c.authBy || "—",
      authorizationNumber: c.authorizationNumber || c.authNumber || "—",
      refName,
      refBHF,
      refHPCSA,
      primaryICD: primaryICD || "—",
      secondaryICDs: secondaryICDs,
    };
  }

  const renderCaseCard = () => {
    if (!attachedCase) return null;
    const v = deriveCaseView(attachedCase);

    return (
      <div className="border rounded mt-1">
        {/* Header — same look & feel as Modal.Header */}
        <div
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center justify-content-between px-3 py-2"
        >
          <h6 className="m-0">Attached Case</h6>
          <i
            className="fa-regular fa-pen-to-square"
            role="button"
            title="Edit case"
            onClick={() => setShowAddCase(true)}
          />
        </div>

        {/* Body — white background */}
        <div className="p-3" style={{ background: "#FFFFFF" }}>
          <Row className="mb-2">
            <Col xs={12} className="text-center">
              <div className="fw-semibold">{v.title}</div>
              <div className="text-muted small">
                {v.start}
                {v.end ? ` to ${v.end}` : ""}
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={6} md={6} className="text-end">
              <p className="mb-1">Referral</p>
              <p className="mb-1">Referral BHF</p>
              <p className="mb-1">Referral HPCSA</p>
              <p className="mb-1">Auth By</p>
              <p className="mb-1">Auth Number</p>
              <p className="mb-0">ICD-10</p>
            </Col>
            <Col xs={6} md={6} className="text-start">
              <p className="mb-1">{v.refName}</p>
              <p className="mb-1">{v.refBHF}</p>
              <p className="mb-1">{v.refHPCSA}</p>
              <p className="mb-1">{v.authorizedBy}</p>
              <p className="mb-1">{v.authorizationNumber}</p>
              <p className="mb-0">
                {v.primaryICD}
                {Array.isArray(v.secondaryICDs) && v.secondaryICDs?.length
                  ? ` | ${v.secondaryICDs.join(", ")}`
                  : ""}
              </p>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="acc-list acc-list-bare">
        {breadcrumbItems && (
          <div className="ms-1 mb-2">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        )}
        <header className="d-flex align-items-center flex-wrap gap-2 mb-3 ms-1 me-1 justify-content-between">
          <h4 className="text-primary fw-normal mb-0">Invoice</h4>
          <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end">
            {invoiceNumber && (
              <span
                className="d-none d-lg-inline-block badge rounded-pill bg-light text-primary border py-2 px-3"
                style={{ fontSize: "0.7rem" }}
              >
                #{invoiceNumber}
              </span>
            )}
            {/* Account tag — coloured per accountTagColor. Mirrors the
                accountTag badge on the Accounts page header. */}
            {account?.accountTagText && account?.accountTagColor && (
              <span
                className="badge rounded-pill py-2 px-3"
                style={{
                  backgroundColor: account.accountTagColor,
                  color: "#fff",
                  fontSize: "0.7rem",
                  whiteSpace: "nowrap",
                }}
                title={account.accountTagText}
              >
                {account.accountTagText}
              </span>
            )}
            {/* Written Off — current invoice status flag. */}
            {invoiceStatus === "Written Off" && (
              <span
                className="badge rounded-pill bg-danger py-2 px-3"
                style={{ fontSize: "0.7rem" }}
                title="Written Off"
              >
                <i className="fa fa-ban" />
                <span className="d-none d-lg-inline ms-1">Written Off</span>
              </span>
            )}
            {/* EDI Disabled — account-level flag plus future end-date.
                Same gating logic as Accounts.jsx + AccountStatements:
                ediDisabled=true AND ediEndDate >= today. */}
            {account?.ediDisabled &&
              account?.ediEndDate &&
              new Date(account.ediEndDate) >=
                new Date(new Date().toISOString().split("T")[0]) && (
                <span
                  className="badge rounded-pill bg-warning text-dark py-2 px-3"
                  style={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}
                  title={`EDI Disabled until ${account.ediEndDate}`}
                >
                  <i className="fa-solid fa-tower-broadcast" />
                  <span className="d-none d-lg-inline ms-1">
                    EDI Disabled until {account.ediEndDate}
                  </span>
                </span>
              )}
            <span
              className={`badge rounded-pill bg-${getStatusVariant(
                invoiceStatus,
              )} py-2 px-3`}
              style={{ fontSize: "0.7rem" }}
            >
              Invoice: {invoiceStatus || "Draft"}
            </span>
          </div>
        </header>

      <main>
        {/* Meta + Case sidebar row.
            - No case: meta block spans full width (lg=12).
            - Case attached: meta shrinks to lg=9, case accordion sits in the
              lg=3 sidebar on desktop (above on mobile via order-* utils).
            Mirrors PostedInvoice.jsx's two-column meta/sidebar layout but
            scoped to the meta area only — the line items below run full width. */}
        <Row className="g-3 mb-3">
          <Col
            xs={12}
            lg={attachedCase ? 9 : 12}
            className={attachedCase ? "order-2 order-lg-1" : ""}
          >
            <div className="posted-invoice-meta p-2 rounded h-100 d-flex flex-column">
          <Row className="g-2">
            {/* --- Account --- */}
            <Col xs={12} md={6} lg={3} className="small">
              <div className="posted-meta-section-title">Account</div>
              <p className="fw-semibold text-primary mb-1">
                {account.accountTitle} {account.accountFullNames}{" "}
                {account.accountLastName}
              </p>
              <p className="mb-0">{account.accountHomeAddress1}</p>
              <p className="mb-0">
                {account.accountHomeAddress2} {account.accountHomeAddressCity}{" "}
                {account.accountHomeAddressPostalCode}
              </p>
              <p className="mb-0">{account.accountTelNumberCell}</p>
              <p className="mb-0">{account.accountEmail}</p>
            </Col>

            {/* --- Patient --- */}
            <Col xs={12} md={6} lg={3} className="small">
              <div className="posted-meta-section-title">Patient</div>
              <Form.Group className="mb-2">
                <Form.Select
                  size="sm"
                  value={selectedPatientId || ""}
                  onChange={(e) => setSelectedPatientId(e.target.value)}
                  isInvalid={!!invoiceErrors.patient}
                >
                  <option value="">Select a patient...</option>
                  {patients?.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.patientFirstName} {p.patientLastName}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {invoiceErrors.patient}
                </Form.Control.Feedback>
              </Form.Group>

              {account.accountMedicalAid ? (
                <>
                  <p className="mb-0 d-flex align-items-center gap-2">
                    <span className="badge bg-primary">Medical Aid</span>
                    <span>{account.accountMedicalAid}</span>
                  </p>

                  {(account.accountMedicalAidPlan ||
                    account.accountMedicalAidOption) && (
                    <p className="mb-0">
                      {account.accountMedicalAidPlan}
                      {account.accountMedicalAidPlan &&
                        account.accountMedicalAidOption &&
                        " | "}
                      {account.accountMedicalAidOption}
                    </p>
                  )}

                  <p className="mb-0">{account.accountMedicalAidNumber}</p>
                </>
              ) : account.accountIODFund ? (
                <>
                  <p className="mb-0 d-flex align-items-center gap-2">
                    {(account.accountIODFundCategory || "").toUpperCase() ===
                    "INSURANCE" ? (
                      <span className="badge bg-info text-dark">INS</span>
                    ) : (
                      <span className="badge bg-warning text-dark">IOD</span>
                    )}
                    <span>{account.accountIODFund}</span>
                  </p>

                  {(account.accountIODPlan || account.accountIODOption) && (
                    <p className="mb-0">
                      {account.accountIODPlan}
                      {account.accountIODPlan &&
                        account.accountIODOption &&
                        " | "}
                      {account.accountIODOption}
                    </p>
                  )}

                  {account.accountIODClaimRefNumber && (
                    <p className="mb-0">
                      Claim Ref: {account.accountIODClaimRefNumber}
                    </p>
                  )}

                  {(account.accountIODFundCategory || "").toUpperCase() !==
                    "INSURANCE" &&
                    account.accountIODEmployer && (
                      <p className="mb-0">
                        Employer: {account.accountIODEmployer}
                      </p>
                    )}

                  {(account.accountIODFundCategory || "").toUpperCase() !==
                    "INSURANCE" &&
                    account.accountIODEmployerWCARegNum && (
                      <p className="mb-0">
                        WCA Reg #: {account.accountIODEmployerWCARegNum}
                      </p>
                    )}
                  {(account.accountIODFundCategory || "").toUpperCase() !==
                    "INSURANCE" &&
                    account.accountIODDateOfInjury && (
                      <p className="mb-0">
                        Date of Injury: {account.accountIODDateOfInjury}
                      </p>
                    )}
                </>
              ) : (
                <p className="mb-0">
                  <span className="badge bg-secondary">Private Patient</span>
                </p>
              )}
            </Col>

            {/* --- Practice & Practitioner --- */}
            <Col xs={12} md={6} lg={3} className="small">
              <div className="posted-meta-section-title">
                Practice &amp; Practitioner
              </div>
              {renderPracticeSection()}
            </Col>

            {/* --- Invoice Info --- */}
            <Col xs={12} md={6} lg={3} className="small">
              <div className="posted-meta-section-title">Invoice Info</div>
              {invoiceNumber && (
                <Row className="mb-1 gx-0">
                  <Col
                    xs={4}
                    className="fw-semibold text-primary text-start"
                  >
                    Invoice:
                  </Col>
                  <Col xs={8} className="text-start">
                    {invoiceNumber}
                  </Col>
                </Row>
              )}
              <Form.Group
                as={Row}
                className="mb-2 gx-0 align-items-center"
                controlId="invoiceDate"
              >
                <Form.Label
                  column
                  xs={4}
                  className="fw-semibold text-primary text-start small mb-0"
                >
                  Date:
                </Form.Label>
                <Col xs={8}>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    readOnly={isPosted}
                    style={isPosted ? { backgroundColor: "#e9ecef" } : {}}
                    isInvalid={!!invoiceErrors.invoiceDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {invoiceErrors.invoiceDate}
                  </Form.Control.Feedback>
                </Col>
              </Form.Group>
              <Row className="mb-1 gx-0">
                <Col
                  xs={4}
                  className="fw-semibold text-primary text-start"
                >
                  Status:
                </Col>
                <Col xs={8} className="text-start">
                  {invoiceStatus}
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Inline action strip — Select ICD-10 / Select Case / Actions sit
              at the bottom of the meta card so the lines section starts higher
              on the page. Thin top border separates this strip from the
              4-up grid above. All three buttons share the same `minWidth`
              so the row reads tidily even with different label lengths. */}
          <div
            className="d-flex flex-wrap gap-2 mt-2 pt-2"
            style={{ borderTop: "1px solid rgba(15, 52, 144, 0.10)" }}
          >
            <Button
              variant="outline-primary"
              size="sm"
              style={{ minWidth: 150 }}
              onClick={() => setShowICDModal(true)}
            >
              Select ICD-10(s)
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              style={{ minWidth: 150 }}
              onClick={() => setShowAddCase(true)}
            >
              {attachedCase ? "Change Case" : "Select Case"}
            </Button>
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                size="sm"
                style={{ minWidth: 150 }}
                id="invoice-actions-dropdown"
                disabled={isPosted}
              >
                <i className="fa-solid fa-gears me-1" />
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu
                align="start"
                className="shadow-sm actions-dropdown"
                renderOnMount
                popperConfig={{
                  strategy: "fixed",
                  modifiers: [
                    {
                      name: "computeStyles",
                      options: { adaptive: false, gpuAcceleration: false },
                    },
                  ],
                }}
              >
                <Dropdown.Item onClick={handleAddLine}>
                  <i className="fa-sharp fa-light fa-pen-line me-2" />
                  Line
                </Dropdown.Item>
                <Dropdown.Item onClick={handleAddServiceLine}>
                  <i className="fa-light fa-clipboard-medical me-2" />
                  Service / Custom Code
                </Dropdown.Item>
                <Dropdown.Item onClick={handleAddTariffLine}>
                  <i className="fa-light fa-book-medical me-2" />
                  Tariff
                </Dropdown.Item>
                <Dropdown.Item onClick={handleAddNappiLine}>
                  <i className="fa-light fa-crutches me-2" />
                  Nappi
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    const hasTariffLine = lines.some(
                      (l) =>
                        (l.type === "tariff" ||
                          !!l.selectedTariff ||
                          !!l.tariffCode) &&
                        !(l.type === "nappi" || !!l.nappiCode),
                    );
                    if (!hasTariffLine) {
                      alert(
                        "Add a tariff line first. Modifiers cannot be applied to NAPPI lines.",
                      );
                      return;
                    }
                    setShowModifier(true);
                  }}
                >
                  <i className="fa-light fa-square-sliders-vertical me-2" />
                  Modifier
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowServiceProvider(true)}>
                  <i className="fa-light fa-user-doctor-hair me-2" />
                  Service Provider
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* ICD-10 chip list — folded inside the meta card so it sits with
              its triggering "Select ICD-10(s)" button. Hidden when empty
              (the button itself signals the action). */}
          {icdSelections?.length > 0 && (
            <ul className="list-unstyled mb-0 small mt-2">
              {icdSelections.map((s, idx) => {
                const code = getIcdCode(s.item);
                const line = getIcdOneLine(s.item);
                return (
                  <li
                    key={code || idx}
                    className="d-flex align-items-center gap-2 mb-1"
                  >
                    <span
                      className={`badge ${
                        s.isPrimary ? "bg-primary" : "bg-secondary"
                      }`}
                    >
                      {s.isPrimary ? "Primary" : "Secondary"}
                    </span>
                    <span
                      className="text-truncate"
                      title={line}
                      style={{ maxWidth: 520 }}
                    >
                      {line}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {invoiceErrors.icd10 && (
            <div className="text-danger small mt-1">{invoiceErrors.icd10}</div>
          )}
        </div>
          </Col>

          {/* Case sidebar — static panel (no collapse). Folder icon + "Case"
              title left, edit pen right; body always visible while a case is
              attached. Sticky on desktop via `.acc-list-sidebar`. */}
          {attachedCase && (
            <Col xs={12} lg={3} className="order-1 order-lg-2">
              <div
                className={`acc-list-sidebar h-100 ${
                  caseCollapsed ? "case-collapsed" : ""
                }`}
              >
                <div
                  className="d-flex align-items-center justify-content-between px-3 py-2"
                  style={{
                    borderBottom: "1px solid rgba(15, 52, 144, 0.10)",
                  }}
                >
                  <span className="posted-meta-section-title mb-0 d-flex align-items-center">
                    <i className="fa-solid fa-folder-open text-primary me-2" />
                    Case
                  </span>
                  <div className="d-flex align-items-center gap-3">
                    <i
                      className="fa-regular fa-pen-to-square text-primary"
                      role="button"
                      title="Edit case"
                      onClick={() => setShowAddCase(true)}
                    />
                    {/* Chevron toggles collapse on mobile; hidden on lg+ via
                        `.case-chevron` CSS rule (acc-list.css). */}
                    <i
                      className={`fa-solid case-chevron text-primary fa-chevron-${
                        caseCollapsed ? "down" : "up"
                      }`}
                      role="button"
                      title={caseCollapsed ? "Expand" : "Collapse"}
                      onClick={() => setCaseCollapsed(!caseCollapsed)}
                    />
                  </div>
                </div>
                <div className="py-2 px-3 case-body">
                  {(() => {
                    const v = deriveCaseView(attachedCase);
                    if (!v) return null;
                    return (
                      <div className="small">
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                Description:
                              </Col>
                              <Col xs={6}>{v.title}</Col>
                            </Row>
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                Doctor:
                              </Col>
                              <Col xs={6}>{v.refName}</Col>
                            </Row>
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                Ref BHF:
                              </Col>
                              <Col xs={6}>{v.refBHF}</Col>
                            </Row>
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                Ref HPCSA:
                              </Col>
                              <Col xs={6}>{v.refHPCSA}</Col>
                            </Row>
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                Authorized By:
                              </Col>
                              <Col xs={6}>{v.authorizedBy}</Col>
                            </Row>
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                Authorization #:
                              </Col>
                              <Col xs={6}>{v.authorizationNumber}</Col>
                            </Row>
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                Start Date:
                              </Col>
                              <Col xs={6}>{v.start}</Col>
                            </Row>
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                End Date:
                              </Col>
                              <Col xs={6}>{v.end || "—"}</Col>
                            </Row>
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                ICD-10 (Primary):
                              </Col>
                              <Col xs={6}>{v.primaryICD}</Col>
                            </Row>
                            <Row className="gx-0 mb-0">
                              <Col
                                xs={6}
                                className="fw-normal text-primary"
                              >
                                ICD-10 (Secondary):
                              </Col>
                              <Col xs={6}>
                                {Array.isArray(v.secondaryICDs) &&
                                v.secondaryICDs.length
                                  ? v.secondaryICDs.join(", ")
                                  : "—"}
                              </Col>
                            </Row>
                          </div>
                        );
                      })()}
                </div>
              </div>
            </Col>
          )}
        </Row>

        {/* Add/Edit Case Modal */}
        <CasePickerPanel
          show={showAddCase}
          onHide={() => setShowAddCase(false)}
          patientId={selectedPatientId}
          existingReferrers={[]}
          currentUserId={selectedPractitionerId}
          invoiceICDSelections={icdSelections}
          onConfirm={handleAttachCase}
        />
        <SoonExpiringCasePrompt
          show={showSoonExpiringPrompt}
          onHide={() => setShowSoonExpiringPrompt(false)}
          cases={soonExpiringCases}
          onUseCase={handleAttachCase}
          onAddNew={() => {
            setShowSoonExpiringPrompt(false);
            setShowAddCase(true);
          }}
        />
        {showModifier && (
          <AddModifierModal
            show={showModifier}
            onHide={() => setShowModifier(false)}
            invoiceLines={lines}
            practiceDisciplineCode={disciplineCode || initialDisciplineCode} // ✅ now it's practitioner-driven (with fallback)
            setLines={setLines}
          />
        )}
        {showServiceProvider && (
          <ServiceProviderModal
            show={showServiceProvider}
            onHide={() => setShowServiceProvider(false)}
            invoiceLines={lines}
            practitioners={practitioners}
            invoicePractitionerId={selectedPractitionerId}
            setLines={setLines}
          />
        )}

        {feedback?.overallWarnings &&
          feedback.overallWarnings.code &&
          feedback.overallWarnings.description && (
            <p className="text-warning fw-bold">
              {feedback.overallWarnings.code} -{" "}
              {feedback.overallWarnings.description}
            </p>
          )}

        {feedback?.overallRejections &&
          feedback.overallRejections.code &&
          feedback.overallRejections.description && (
            <p className="text-danger fw-bold">
              Claim Rejected: {feedback.overallRejections.code} -{" "}
              {feedback.overallRejections.description} - Please correct the
              claim and or patient information and re-submit
            </p>
          )}

        {/* Lines + Totals card — same surface as the meta block above so the
            two cards read as a matched pair. */}
        <div className="posted-invoice-meta p-3 rounded mb-3">
        {/* Headers */}
        <Col md={12} xs={12} sm={12} className="mb-2 posted-lines">
          <Row className="align-items-center mb-1 g-1 inv-desktop-flex">
            <Col md={2} className="col-responsive posted-meta-section-title text-center px-1">
              Appointment | Date
            </Col>
            <Col md={1} className="col-responsive posted-meta-section-title text-center px-1">
              Prim ICD10
            </Col>
            <Col md={1} className="col-responsive posted-meta-section-title text-center px-1">
              Sec ICD10
            </Col>
            <Col md={1} className="col-responsive posted-meta-section-title text-center px-1">
              Tar | Nap | (Mod)
            </Col>
            <Col md={4} className="col-responsive posted-meta-section-title text-center px-1">
              Description
            </Col>
            <Col md={1} className="col-responsive posted-meta-section-title text-center px-1">
              Unit(s)
            </Col>
            <Col md={1} className="col-responsive posted-meta-section-title text-center px-1">
              Rate (R)
            </Col>
            <Col md={1} className="col-responsive posted-meta-section-title text-center px-1">
              Priv | Del
            </Col>
          </Row>

          {/* Lines */}
          {lines.map((line, index) => (
            <div key={index}>
              {/* Tariff search row */}
              {line.type === "tariff" && !line.selectedTariff && (
                <Row className="align-items-center mt-2 mb-2">
                  <Col md={4}>
                    <TariffSearch
                      practiceNumber={selectedPractice?.practiceNumber}
                      onTariffCodeSelect={(tariff) =>
                        handleTariffLineSelect(index, tariff)
                      }
                    />
                  </Col>
                  <Col md="auto">
                    <Button
                      variant="link"
                      onClick={() => handleRemoveLine(index)}
                      className="p-0"
                      aria-label="Delete line"
                      title="Delete line"
                    >
                      <i className="fa fa-trash text-danger" />
                    </Button>
                  </Col>
                </Row>
              )}

              {/* Nappi search row */}
              {line.type === "nappi" && !line.selectedNappi && (
                <Row className="align-items-center mt-2 mb-2">
                  <Col md={4}>
                    <NappiSearch
                      onNappiSelect={(n) => handleNappiLineSelect(index, n)}
                    />
                  </Col>
                  <Col md="auto">
                    <Button
                      variant="link"
                      onClick={() => handleRemoveLine(index)}
                      className="p-0"
                      aria-label="Delete line"
                      title="Delete line"
                    >
                      <i className="fa fa-trash text-danger" />
                    </Button>
                  </Col>
                </Row>
              )}

              {/* Service selection row */}
              {line.type === "service" && !line.selectedService && (
                <Row className="align-items-center mb-2">
                  <Col md={6}>
                    <ServiceLinePicker
                      services={services}
                      customCodes={customCodes}
                      onSelect={(svcId) => handleServiceSelect(index, svcId)}
                    />
                  </Col>
                  <Col md="auto">
                    <Button
                      variant="link"
                      onClick={() => handleRemoveLine(index)}
                      className="p-0"
                      aria-label="Delete line"
                      title="Delete line"
                    >
                      <i className="fa fa-trash text-danger" />
                    </Button>
                  </Col>
                </Row>
              )}

              {/* Main row */}
              {(line.type === "custom" ||
                line.selectedService ||
                line.selectedTariff ||
                line.selectedNappi) && (
                <>
                  <Row className="align-items-center mb-1 g-1 inv-desktop-flex">
                    {/* Appt / Date */}
                    <Col md={2}>
                      <>
                        <AppointmentDateInput
                          patientId={line.patientId}
                          fetchAppointments={getAppointmentsByPatientId}
                          initial={lineApptInitial}
                          value={lineApptValue(line)}
                          error={
                            lineErrors?.[index]?.appointmentDate ||
                            lineErrors?.[index]?.appointmentId ||
                            ""
                          }
                          onAppointmentSelect={(sel) => {
                            if (!sel) {
                              const patch = {
                                useCustomDate: false,
                                appointmentId: "",
                                appointmentStartSet: null,
                                appointmentEndSet: null,
                                appointmentDateSet: null,
                                appointmentCustomStart: null,
                                appointmentCustomEnd: null,
                                appointmentDateCustom: null,
                                appointmentDate: null,
                              };

                              handleLineAppointmentChange(index, patch);
                              const gid = lines[index]?.groupId;
                              if (gid) refreshMSRForGroup(gid, patch);
                              else refreshMSRForLine(index, patch);
                              return;
                            }

                            const isNormalized =
                              "appointmentStartSet" in sel ||
                              "appointmentCustomStart" in sel ||
                              "appointmentEndSet" in sel ||
                              "appointmentCustomEnd" in sel;

                            if (isNormalized) {
                              // sel already matches your line fields
                              const patch = sel;

                              handleLineAppointmentChange(index, patch);
                              refreshMSRForLine(index, patch);
                              return;
                            }

                            const toISO = (d, t) =>
                              d && t ? `${d}T${t}` : null;

                            let patch;

                            if (sel.useCustomDate) {
                              patch = {
                                useCustomDate: true,
                                appointmentId: null,
                                appointmentStartSet: null,
                                appointmentEndSet: null,
                                appointmentDateSet: null,
                                appointmentCustomStart: toISO(
                                  sel.startDate,
                                  sel.startTime,
                                ),
                                appointmentCustomEnd: toISO(
                                  sel.endDate,
                                  sel.endTime,
                                ),
                                appointmentDateCustom: sel.startDate || null,
                                appointmentDate: sel.startDate || null,
                              };
                            } else {
                              patch = {
                                useCustomDate: false,
                                appointmentId:
                                  sel.appointmentId ||
                                  lines[index]?.appointmentId ||
                                  "",
                                appointmentStartSet: toISO(
                                  sel.startDate,
                                  sel.startTime,
                                ),
                                appointmentEndSet: toISO(
                                  sel.endDate,
                                  sel.endTime,
                                ),
                                appointmentDateSet: sel.startDate || null,
                                appointmentCustomStart: null,
                                appointmentCustomEnd: null,
                                appointmentDateCustom: null,
                                appointmentDate: sel.startDate || null,
                              };
                            }

                            handleLineAppointmentChange(index, patch);
                            refreshMSRForLine(index, patch);
                          }}
                        />
                        {/* ALWAYS show error message if it exists */}
                        {(lineErrors?.[index]?.appointmentDate ||
                          lineErrors?.[index]?.appointmentId) && (
                          <div className="text-danger small mt-1">
                            {lineErrors?.[index]?.appointmentDate ||
                              lineErrors?.[index]?.appointmentId}
                          </div>
                        )}
                      </>
                    </Col>

                    {/* Primary ICD10 */}
                    <Col md={1} className="px-1 d-flex align-items-center">
                      {primaryIcds?.length > 0 && (
                        <Form.Select
                          className="w-100"
                          style={{ Height: "31px" }}
                          value={
                            line.icd101 ||
                            (primaryIcds[0] ? getIcdCode(primaryIcds[0]) : "")
                          }
                          onChange={(e) =>
                            handleLineChange(index, "icd101", e.target.value)
                          }
                          isInvalid={!!lineErrors?.[index]?.icd101}
                        >
                          {primaryIcds.map((icd, idx) => {
                            const codeToUse = getIcdCode(icd);
                            const left =
                              icd.ICD10_3_Code || icd.ICD10_Code || "";
                            const right =
                              icd.ICD10_3_Code && icd.ICD10_Code
                                ? ` | ${icd.ICD10_Code}`
                                : "";
                            return (
                              <option key={idx} value={codeToUse}>
                                {left}
                                {right}
                              </option>
                            );
                          })}
                        </Form.Select>
                      )}
                      <Form.Control.Feedback type="invalid">
                        {lineErrors?.[index]?.icd101}
                      </Form.Control.Feedback>
                    </Col>

                    {/* Secondary ICD10 */}
                    <Col md={1} className="px-1 d-flex align-items-center">
                      <div className="w-100">
                        <CompactMultiSelect
                          id={`sec-icd-${index}`}
                          value={
                            Array.isArray(line.icd10Secondary)
                              ? line.icd10Secondary
                              : []
                          }
                          onChange={(vals) =>
                            handleLineChange(index, "icd10Secondary", vals)
                          }
                          options={secondaryIcds.map((icd) => {
                            const val = getIcdCode(icd);
                            const left =
                              icd.ICD10_3_Code || icd.ICD10_Code || "";
                            const right =
                              icd.ICD10_3_Code && icd.ICD10_Code
                                ? ` | ${icd.ICD10_Code}`
                                : "";
                            return {
                              value: val,
                              label: `${left}${right}`,
                              title: getIcdOneLine(icd),
                            };
                          })}
                        />
                        {lineErrors?.[index]?.icd10Secondary && (
                          <div className="text-danger small mt-1">
                            {lineErrors?.[index]?.icd10Secondary}
                          </div>
                        )}
                      </div>
                    </Col>

                    {/* Tariff | Nappi | (Modifier) */}
                    <Col md={1} className="col-responsive px-1">
                      {(() => {
                        const isNappi =
                          line.type === "nappi" || !!line.nappiCode;

                        if (isNappi) {
                          return (
                            <Form.Control
                              type="text"
                              className="text-center"
                              size="sm"
                              value={line.nappiCode || ""}
                              onChange={(e) =>
                                handleLineChange(
                                  index,
                                  "nappiCode",
                                  e.target.value,
                                )
                              }
                            />
                          );
                        }

                        const modCodes = Array.isArray(line.modifier)
                          ? line.modifier
                              .map((m) => (typeof m === "string" ? m : m.code))
                              .filter(Boolean)
                          : [];
                        const display = modCodes?.length
                          ? `${line.tariffCode || ""} (${modCodes.join(", ")})`
                          : line.tariffCode || "";

                        if (modCodes?.length) {
                          return (
                            <Form.Control
                              type="text"
                              className="text-center"
                              size="sm"
                              value={display}
                              readOnly
                              title={display}
                              style={{
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            />
                          );
                        }

                        return (
                          <Form.Control
                            type="text"
                            className="text-center"
                            size="sm"
                            value={line.tariffCode || ""}
                            onChange={(e) =>
                              handleLineChange(
                                index,
                                "tariffCode",
                                e.target.value,
                              )
                            }
                          />
                        );
                      })()}
                    </Col>

                    {/* Description */}
                    <Col md={4} className="col-responsive px-1">
                      <Form.Control
                        type="text"
                        size="sm"
                        value={(line.description || "").replace(
                          /\s*\(\d{1,3}\)$/,
                          "",
                        )}
                        title={(() => {
                          const desc = line.description || "";
                          const spName = line.serviceProvider
                            ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`
                            : selectedPract
                              ? `${selectedPract.firstName} ${selectedPract.lastName}`
                              : "";
                          return spName
                            ? `${desc}\nService Provider: ${spName}`
                            : desc;
                        })()}
                        onChange={(e) =>
                          handleLineChange(index, "description", e.target.value)
                        }
                        isInvalid={!!lineErrors?.[index]?.description}
                      />
                      <Form.Control.Feedback type="invalid">
                        {lineErrors?.[index]?.description}
                      </Form.Control.Feedback>
                    </Col>

                    {/* Quantity */}
                    <Col md={1} className="col-responsive px-1">
                      <Form.Control
                        type="number"
                        size="sm"
                        className="text-center"
                        value={line.quantity || 1}
                        onChange={(e) =>
                          handleLineChange(index, "quantity", e.target.value)
                        }
                        isInvalid={!!lineErrors?.[index]?.quantity}
                      />
                      <Form.Control.Feedback type="invalid">
                        {lineErrors?.[index]?.quantity}
                      </Form.Control.Feedback>
                    </Col>

                    {/* Rate */}
                    <Col md={1} className="col-responsive px-1">
                      <Form.Control
                        type="text"
                        size="sm"
                        className="text-end"
                        value={line.rate ?? "0.00"}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const cleaned = raw
                            .replace(",", ".") // treat comma as decimal
                            .replace(/[^0-9.]/g, "");
                          handleLineChange(index, "rate", cleaned);
                        }}
                        onBlur={(e) => {
                          const num = normalizeToNumber(e.target.value);
                          const formatted = num.toFixed(2); // always "." and 2 decimals
                          handleLineChange(index, "rate", formatted);
                        }}
                        readOnly={isPosted}
                        style={isPosted ? { backgroundColor: "#e9ecef" } : {}}
                        isInvalid={!!lineErrors?.[index]?.rate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {lineErrors?.[index]?.rate}
                      </Form.Control.Feedback>
                    </Col>

                    {/* Private & Delete */}
                    <Col md={1} className="col-responsive px-1">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <Form.Check
                          type="checkbox"
                          checked={line.privatePayment}
                          onChange={(e) =>
                            handleLineChange(
                              index,
                              "privatePayment",
                              e.target.checked,
                            )
                          }
                          disabled={isPosted}
                          style={{
                            backgroundColor: isPosted
                              ? "#e9ecef"
                              : "transparent",
                            marginBottom: 0,
                          }}
                        />
                        <Button
                          variant="link"
                          onClick={() => handleRemoveLine(index)}
                          className="p-0"
                          disabled={isPosted}
                          aria-label="Delete line"
                          title="Delete line"
                        >
                          <i className="fa fa-trash text-danger" />
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {/* Mobile 2-row layout — visible only below md breakpoint.
                      Outer wrapper uses the same `.acc-card.posted-line-card`
                      vocabulary as PostedInvoice's mobile per-line cards, so
                      the brand surface (border / shadow / radius) reads
                      consistently across read-mode and edit-mode. */}
                  <div className="inv-mobile acc-card posted-line-card mb-2">
                    {/* Mobile Row 1: Appointment/Date, Tariff/Nappi/Modifier, Description */}
                    <Row className="align-items-start mb-2 g-1">
                      {/* Appointment / Date */}
                      <Col xs={12} sm={5}>
                        <div className="acc-card-label text-center mb-1">
                          Appointment | Date
                        </div>
                        <AppointmentDateInput
                          patientId={line.patientId}
                          fetchAppointments={getAppointmentsByPatientId}
                          initial={lineApptInitial}
                          value={lineApptValue(line)}
                          error={
                            lineErrors?.[index]?.appointmentDate ||
                            lineErrors?.[index]?.appointmentId ||
                            ""
                          }
                          onAppointmentSelect={(sel) => {
                            if (!sel) {
                              const patch = {
                                useCustomDate: false,
                                appointmentId: "",
                                appointmentStartSet: null,
                                appointmentEndSet: null,
                                appointmentDateSet: null,
                                appointmentCustomStart: null,
                                appointmentCustomEnd: null,
                                appointmentDateCustom: null,
                                appointmentDate: null,
                              };
                              handleLineAppointmentChange(index, patch);
                              const gid = lines[index]?.groupId;
                              if (gid) refreshMSRForGroup(gid, patch);
                              else refreshMSRForLine(index, patch);
                              return;
                            }
                            const isNormalized =
                              "appointmentStartSet" in sel ||
                              "appointmentCustomStart" in sel ||
                              "appointmentEndSet" in sel ||
                              "appointmentCustomEnd" in sel;
                            if (isNormalized) {
                              handleLineAppointmentChange(index, sel);
                              refreshMSRForLine(index, sel);
                              return;
                            }
                            const toISO = (d, t) =>
                              d && t ? `${d}T${t}` : null;
                            let patch;
                            if (sel.useCustomDate) {
                              patch = {
                                useCustomDate: true,
                                appointmentId: null,
                                appointmentStartSet: null,
                                appointmentEndSet: null,
                                appointmentDateSet: null,
                                appointmentCustomStart: toISO(
                                  sel.startDate,
                                  sel.startTime,
                                ),
                                appointmentCustomEnd: toISO(
                                  sel.endDate,
                                  sel.endTime,
                                ),
                                appointmentDateCustom: sel.startDate || null,
                                appointmentDate: sel.startDate || null,
                              };
                            } else {
                              patch = {
                                useCustomDate: false,
                                appointmentId:
                                  sel.appointmentId ||
                                  lines[index]?.appointmentId ||
                                  "",
                                appointmentStartSet: toISO(
                                  sel.startDate,
                                  sel.startTime,
                                ),
                                appointmentEndSet: toISO(
                                  sel.endDate,
                                  sel.endTime,
                                ),
                                appointmentDateSet: sel.startDate || null,
                                appointmentCustomStart: null,
                                appointmentCustomEnd: null,
                                appointmentDateCustom: null,
                                appointmentDate: sel.startDate || null,
                              };
                            }
                            handleLineAppointmentChange(index, patch);
                            refreshMSRForLine(index, patch);
                          }}
                        />
                        {(lineErrors?.[index]?.appointmentDate ||
                          lineErrors?.[index]?.appointmentId) && (
                          <div className="text-danger small mt-1">
                            {lineErrors?.[index]?.appointmentDate ||
                              lineErrors?.[index]?.appointmentId}
                          </div>
                        )}
                      </Col>

                      {/* Tariff | Nappi | (Modifier) */}
                      <Col xs={4} sm={2}>
                        <div className="acc-card-label text-center mb-1">T | N | (M)</div>
                        {(() => {
                          const isNappi =
                            line.type === "nappi" || !!line.nappiCode;
                          if (isNappi) {
                            return (
                              <Form.Control
                                type="text"
                                className="text-center"
                                size="sm"
                                value={line.nappiCode || ""}
                                onChange={(e) =>
                                  handleLineChange(
                                    index,
                                    "nappiCode",
                                    e.target.value,
                                  )
                                }
                              />
                            );
                          }
                          const modCodes = Array.isArray(line.modifier)
                            ? line.modifier
                                .map((m) =>
                                  typeof m === "string" ? m : m.code,
                                )
                                .filter(Boolean)
                            : [];
                          const display = modCodes?.length
                            ? `${line.tariffCode || ""} (${modCodes.join(", ")})`
                            : line.tariffCode || "";
                          if (modCodes?.length) {
                            return (
                              <Form.Control
                                type="text"
                                className="text-center"
                                size="sm"
                                value={display}
                                readOnly
                                title={display}
                                style={{
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                }}
                              />
                            );
                          }
                          return (
                            <Form.Control
                              type="text"
                              className="text-center"
                              size="sm"
                              value={line.tariffCode || ""}
                              onChange={(e) =>
                                handleLineChange(
                                  index,
                                  "tariffCode",
                                  e.target.value,
                                )
                              }
                            />
                          );
                        })()}
                      </Col>

                      {/* Description */}
                      <Col xs={8} sm={5}>
                        <div className="acc-card-label text-center mb-1">Description</div>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={(line.description || "").replace(
                            /\s*\(\d{1,3}\)$/,
                            "",
                          )}
                          title={(() => {
                            const desc = line.description || "";
                            const spName = line.serviceProvider
                              ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`
                              : selectedPract
                                ? `${selectedPract.firstName} ${selectedPract.lastName}`
                                : "";
                            return spName
                              ? `${desc}\nService Provider: ${spName}`
                              : desc;
                          })()}
                          onChange={(e) =>
                            handleLineChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          isInvalid={!!lineErrors?.[index]?.description}
                        />
                        <Form.Control.Feedback type="invalid">
                          {lineErrors?.[index]?.description}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>

                    {/* Mobile Row 2: Primary ICD-10, Secondary ICD-10, Units, Rate, Private/Delete */}
                    <Row className="align-items-center g-1 justify-content-end">
                      {/* Primary ICD10 */}
                      <Col xs={6} sm={2}>
                        <div className="acc-card-label text-center mb-1">Prim ICD10</div>
                        {primaryIcds?.length > 0 && (
                          <Form.Select
                            className="w-100"
                            size="sm"
                            value={
                              line.icd101 ||
                              (primaryIcds[0] ? getIcdCode(primaryIcds[0]) : "")
                            }
                            onChange={(e) =>
                              handleLineChange(index, "icd101", e.target.value)
                            }
                            isInvalid={!!lineErrors?.[index]?.icd101}
                          >
                            {primaryIcds.map((icd, idx) => {
                              const codeToUse = getIcdCode(icd);
                              const left =
                                icd.ICD10_3_Code || icd.ICD10_Code || "";
                              const right =
                                icd.ICD10_3_Code && icd.ICD10_Code
                                  ? ` | ${icd.ICD10_Code}`
                                  : "";
                              return (
                                <option key={idx} value={codeToUse}>
                                  {left}
                                  {right}
                                </option>
                              );
                            })}
                          </Form.Select>
                        )}
                        <Form.Control.Feedback type="invalid">
                          {lineErrors?.[index]?.icd101}
                        </Form.Control.Feedback>
                      </Col>

                      {/* Secondary ICD10 */}
                      <Col xs={6} sm={2}>
                        <div className="acc-card-label text-center mb-1">Sec ICD10</div>
                        <div className="w-100">
                          <CompactMultiSelect
                            id={`sec-icd-mob-${index}`}
                            value={
                              Array.isArray(line.icd10Secondary)
                                ? line.icd10Secondary
                                : []
                            }
                            onChange={(vals) =>
                              handleLineChange(index, "icd10Secondary", vals)
                            }
                            options={secondaryIcds.map((icd) => {
                              const val = getIcdCode(icd);
                              const left =
                                icd.ICD10_3_Code || icd.ICD10_Code || "";
                              const right =
                                icd.ICD10_3_Code && icd.ICD10_Code
                                  ? ` | ${icd.ICD10_Code}`
                                  : "";
                              return {
                                value: val,
                                label: `${left}${right}`,
                                title: getIcdOneLine(icd),
                              };
                            })}
                          />
                          {lineErrors?.[index]?.icd10Secondary && (
                            <div className="text-danger small mt-1">
                              {lineErrors?.[index]?.icd10Secondary}
                            </div>
                          )}
                        </div>
                      </Col>

                      {/* Units */}
                      <Col xs={2} sm={2}>
                        <div className="acc-card-label text-center mb-1">Unit(s)</div>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-center"
                          value={line.quantity || 1}
                          onChange={(e) =>
                            handleLineChange(index, "quantity", e.target.value)
                          }
                          isInvalid={!!lineErrors?.[index]?.quantity}
                        />
                        <Form.Control.Feedback type="invalid">
                          {lineErrors?.[index]?.quantity}
                        </Form.Control.Feedback>
                      </Col>

                      {/* Rate */}
                      <Col xs={6} sm={4}>
                        <div className="acc-card-label text-center mb-1">
                          Rate (R)
                        </div>
                        <Form.Control
                          type="text"
                          size="sm"
                          className="text-end"
                          style={isPosted ? { backgroundColor: "#e9ecef" } : {}}
                          value={line.rate ?? "0.00"}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const cleaned = raw
                              .replace(",", ".")
                              .replace(/[^0-9.]/g, "");
                            handleLineChange(index, "rate", cleaned);
                          }}
                          onBlur={(e) => {
                            const num = normalizeToNumber(e.target.value);
                            handleLineChange(index, "rate", num.toFixed(2));
                          }}
                          readOnly={isPosted}
                          isInvalid={!!lineErrors?.[index]?.rate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {lineErrors?.[index]?.rate}
                        </Form.Control.Feedback>
                      </Col>

                      {/* Private & Delete */}
                      <Col xs={4} sm={2}>
                        <div className="acc-card-label text-center mb-1">Priv | Del</div>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <Form.Check
                            type="checkbox"
                            checked={line.privatePayment}
                            onChange={(e) =>
                              handleLineChange(
                                index,
                                "privatePayment",
                                e.target.checked,
                              )
                            }
                            disabled={isPosted}
                            style={{
                              backgroundColor: isPosted
                                ? "#e9ecef"
                                : "transparent",
                              marginBottom: 0,
                            }}
                          />
                          <Button
                            variant="link"
                            onClick={() => handleRemoveLine(index)}
                            className="p-0"
                            disabled={isPosted}
                            aria-label="Delete line"
                            title="Delete line"
                          >
                            <i className="fa fa-trash text-danger" />
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </>
              )}

              {/* Line feedback */}
              {feedback &&
                feedback.lineResults &&
                feedback.lineResults[index] &&
                (feedback.lineResults[index].rejection ? (
                  <p className="text-danger fw-bold">
                    {feedback.lineResults[index]?.status}-{" "}
                    {feedback.lineResults[index].rejection.code} -{" "}
                    {feedback.lineResults[index].rejection.description}
                  </p>
                ) : feedback.lineResults[index].warning ? (
                  <p className="text-warning fw-bold">
                    {feedback.lineResults[index]?.status}-{" "}
                    {feedback.lineResults[index].warning.code} -{" "}
                    {feedback.lineResults[index].warning.description}
                  </p>
                ) : (
                  <p className="text-success fw-bold">
                    {feedback.lineResults[index]?.status}-{" "}
                  </p>
                ))}
            </div>
          ))}
        </Col>

        <hr className="posted-card-divider" />

        {/* Totals — read-only, styled to match PostedInvoice's acc-list-totals
            block. Sits inside the lines+totals card as its 2nd section. The
            Col width is narrower than PostedInvoice's (lg=3 vs lg=5) because
            CreateInvoice's lines card spans the full page width, so a wider
            Col would push label + amount too far apart. */}
        <Row className="mx-0">
          <Col xs={12} sm={{ span: 6, offset: 6 }} lg={{ span: 3, offset: 9 }}>
            <div className="acc-list-totals">
              <div
                className="d-flex justify-content-between align-items-center py-1"
                style={{
                  borderBottom: "1px solid rgba(15, 52, 144, 0.10)",
                }}
              >
                <span className="posted-meta-section-title mb-0">
                  Total Medical Aid
                </span>
                <span className="fw-semibold text-primary acc-list-num">
                  R {formatAmount(totalMedicalAidAmount)}
                </span>
              </div>
              <div
                className="d-flex justify-content-between align-items-center py-1"
                style={{
                  borderBottom: "1px solid rgba(15, 52, 144, 0.10)",
                }}
              >
                <span className="posted-meta-section-title mb-0">
                  Total Patient
                </span>
                <span className="fw-semibold text-primary acc-list-num">
                  R {formatAmount(totalPatientAmount)}
                </span>
              </div>
              <div
                className="d-flex justify-content-between align-items-center py-1 mt-1"
                style={{
                  borderBottom: "2px solid rgba(15, 52, 144, 0.25)",
                }}
              >
                <span className="posted-meta-section-title mb-0">
                  Invoice Total
                </span>
                <span className="fw-semibold text-primary acc-list-num">
                  R {formatAmount(totalAmount)}
                </span>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </main>

      <footer className="mb-5">
        <Row className="g-2 align-items-center">
          <Col xs={12} md={6} className="d-flex align-items-center">
            {showCompleteApptCheckbox && (
              <Form.Check
                type="checkbox"
                id="invoice-complete-appointment"
                label="Complete Appointment"
                checked={completeAppointmentChecked}
                onChange={(e) =>
                  setCompleteAppointmentChecked(e.target.checked)
                }
              />
            )}
          </Col>
          <Col
            xs={12}
            md={6}
            className="d-flex flex-wrap justify-content-md-end justify-content-start gap-2"
          >
            {!invoiceNumber ? (
              <>
                <Button
                  variant="primary"
                  style={{ minWidth: 140 }}
                  onClick={() =>
                    runGuarded("savePostNew", (start, end) =>
                      saveAndPostNewInvoice(start, end),
                    )
                  }
                  disabled={!!busy}
                >
                  {busy === "savePostNew" ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Posting…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-file-invoice me-1" />
                      Save &amp; Post
                    </>
                  )}
                </Button>

                <Button
                  variant="primary"
                  style={{ minWidth: 140 }}
                  onClick={() =>
                    runGuarded("saveNew", (start, end) =>
                      saveNewInvoice(start, end),
                    )
                  }
                  disabled={!!busy}
                >
                  {busy === "saveNew" ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-floppy-disk me-1" />
                      Save Invoice
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="primary"
                  style={{ minWidth: 140 }}
                  onClick={() =>
                    runGuarded("update", (start, end) =>
                      updateExistingInvoiceAndClaim(start, end),
                    )
                  }
                  disabled={!!busy}
                >
                  {busy === "update" ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-pen-to-square me-1" />
                      Save
                    </>
                  )}
                </Button>

                {invoice?.status === "Pending" && (
                  <>
                    <Button
                      variant="primary"
                      style={{ minWidth: 140 }}
                      onClick={() =>
                        runGuarded("post", (start, end) =>
                          handlePost(start, end),
                        )
                      }
                      disabled={!!busy}
                    >
                      {busy === "post" ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Posting…
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-file-invoice me-1" />
                          Save &amp; Post
                        </>
                      )}
                    </Button>

                    <Button
                      variant="danger"
                      style={{ minWidth: 140 }}
                      onClick={() => setShowDeleteModal(true)}
                      disabled={!!busy}
                    >
                      <i className="fa-solid fa-trash me-1" />
                      Delete
                    </Button>
                  </>
                )}
              </>
            )}
          </Col>
        </Row>
      </footer>
      </div>

      {/* ICD10 modal */}
      <ICD10PickerModal
        show={showICDModal}
        onHide={() => setShowICDModal(false)}
        initialSelected={icdSelections}
        onConfirm={(selected) => {
          setShowICDModal(false);

          const mergedICDs = selected.map((s) => {
            const code = getIcdCode(s.item);
            const previous = selectedICD10s.find((old) => {
              const oldCode = old.item ? getIcdCode(old.item) : getIcdCode(old); // fallback if item is flattened
              console.log("oldCode:", oldCode, "code:", code);
              return oldCode === code;
            });

            console.log("old selectedICD10s:", selectedICD10s);
            return {
              item: s.item,
              isPrimary: !!s.isPrimary,
              isPMB: previous?.isPMB ?? s.isPMB ?? false,
              pmbAuthNo: previous?.pmbAuthNo ?? s.pmbAuthNo ?? "",
              pmbStartDate: previous?.pmbStartDate ?? s.pmbStartDate ?? "",
              pmbEndDate: previous?.pmbEndDate ?? s.pmbEndDate ?? "",
              useOnInvoice: previous?.useOnInvoice ?? s.useOnInvoice ?? false,
            };
          });

          // ✅ Update both icdSelections and selectedICD10s with enriched data
          setIcdSelections(
            mergedICDs.map((s) => ({
              item: s.item,
              isPrimary: !!s.isPrimary,
              isPMB: s.isPMB || false,
              pmbAuthNo: s.pmbAuthNo,
              pmbStartDate: s.pmbStartDate,
              pmbEndDate: s.pmbEndDate,
              useOnInvoice: s.useOnInvoice,
            })),
          );

          setSelectedICD10s(mergedICDs);
          // Update lines with the first primary ICD10 code
          const firstPrimaryCode = getIcdCode(
            mergedICDs.find((s) => s.isPrimary)?.item,
          );
          console.log("mergedICDs", mergedICDs);
          if (firstPrimaryCode) {
            setLines((prev) =>
              prev.map((line) =>
                !line.icd101 ? { ...line, icd101: firstPrimaryCode } : line,
              ),
            );
          }
        }}
      />

      {/* Per-line appointment modal (kept as-is for future use) */}
      <InvoiceAppointmentModal
        patientId={
          editingLineIndex != null
            ? lines[editingLineIndex]?.patientId || selectedPatientId
            : selectedPatientId
        }
        initial={lineApptInitial}
        fetchAppointments={getAppointmentsByPatientId}
        onConfirm={(sel) => {
          const patch = buildLineApptPatch(sel);

          if (editingLineIndex != null) {
            setLineFields(editingLineIndex, patch);
            if (editingLineIndex === 0) {
              setDefaultAppointmentTemplate(
                createDefaultTemplate({ ...lines[0], ...patch }),
              );
            }
          }
          setShowLineApptModal(false);
        }}
        error={apptErrorFor(editingLineIndex)} // <-- this is the correct prop
        show={showLineApptModal}
        onHide={() => setShowLineApptModal(false)}
      />

      {/* Email modal */}
      {showEmailInvoiceModal && invoiceBlob && (
        <EmailInvoiceModal
          show={showEmailInvoiceModal}
          onHide={() => setShowEmailInvoiceModal(false)}
          defaultEmail={account.accountEmail}
          email2={account.accountEmail2}
          email3={account.accountEmail3}
          email4={account.accountEmail4}
          email5={account.accountEmail5}
          onSend={async ({
            email,
            email2,
            email3,
            email4,
            email5,
            type = "invoice",
          }) => {
            try {
              // Collect all non-empty emails into an array
              const emails = [email, email2, email3, email4, email5]
                .map((e) => (e || "").trim())
                .filter(Boolean);

              const result = await sendInvoiceByEmail({
                emails,
                type,
                pdfBlob: invoiceBlob,
                accountId: account._id,
              });

              if (result.error) {
                throw new Error(result.error);
              }

              console.log("✅ Invoice emailed to", emails.join(", "));
            } catch (err) {
              console.error("⛔ Failed to send invoice:", err);
              throw err;
            }
          }}
        />
      )}

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="sm"
      >
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            Delete Invoice
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this pending invoice
          {invoiceNumber ? ` (${invoiceNumber})` : ""}?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteInvoice}
            disabled={busy === "delete"}
          >
            {busy === "delete" ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Deleting…
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </>
  );
}

export default CreateInvoiceModal;
